import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, setDoc, updateDoc, deleteDoc, addDoc, query, orderBy, limit, runTransaction, getDocs, getDoc, where, writeBatch, increment } from 'firebase/firestore';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
    // --- Mock Database / State ---
    const [isAppReady, setIsAppReady] = useState(true); // INSTANT LOAD FIX

    // Removed artificial 1000ms timer
    // useEffect(() => {
    //     const timer = setTimeout(() => setIsAppReady(true), 1000);
    //     return () => clearTimeout(timer);
    // }, []);

    // 1. Commission/System Settings
    const [settings, setSettings] = useState({
        matchingBonus: 10,
        minWithdrawal: 500,
        registrationsEnabled: true,
        payoutsEnabled: true,
        commissionLevels: [5, 4, 3, 2, 1, 1, 0.5],
        selfPurchaseBonus: 10,
        inrPerBv: 1,
        paymentSettings: {
            qrImage: '',
            upiId: '',
            instructions: 'Please pay the total amount using the QR code or UPI ID and upload the screenshot.'
        }
    });
    const [kits, setKits] = useState([]);
    const [products, setProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [payouts, setPayouts] = useState([]);

    // Robust User Finding Utility
    const findUserById = (searchId) => {
        if (!searchId) return null;
        const s = String(searchId).trim().toUpperCase();
        // Try direct match, then "PC" prefix handle
        return users.find(u => {
            const uid = String(u.id || '').toUpperCase();
            const fid = String(u.firestoreId || '').toUpperCase();
            if (uid === s || fid === s) return true;
            // Handle cross-matching (e.g. searching "1003" finds "PC1003")
            if (!s.startsWith('PC') && uid === `PC${s}`) return true;
            if (s.startsWith('PC') && uid === s.replace('PC', '')) return true;
            return false;
        });
    };
    const [commissionLogs, setCommissionLogs] = useState([]);
    const [stockLogs, setStockLogs] = useState([]);
    const [assemblyLogs, setAssemblyLogs] = useState([]);
    const [legalDocs, setLegalDocs] = useState([]);
    const [contactMessages, setContactMessages] = useState([]);
    const [expenses, setExpenses] = useState([]); // NEW: Operational Expenses
    const [siteContent, setSiteContent] = useState(() => {
        const defaults = {
            home: {
                heroTitle: 'Power Clean India',
                heroSubtitle: 'Join the Revolution with Premium Quality Cleaning Products.',
                heroImage: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
                mlmTitle: 'Power Clean Business Plan',
                mlmSubtitle: 'The most rewarding tree-based income structure in India. Start small, grow big.',
                mlmTreeImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
                mlmProjectionImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
                features: [
                    { title: 'Premium Quality', desc: 'Lab-tested formulas for maximum effectiveness.', icon: 'Shield' },
                    { title: 'Eco-Friendly', desc: 'Safe for your family and the environment.', icon: 'Leaf' },
                    { title: 'Direct Selling', desc: 'Buy directly and save big on every purchase.', icon: 'ShoppingBag' }
                ],
                incomeStats: [
                    { label: 'Weekly Payouts', value: '4x' },
                    { label: 'Growth Rate', value: '300%' },
                    { label: 'Rewards', value: '50+' }
                ],
                trustBar: [
                    { label: 'Happy Customers', value: '10,000+' },
                    { label: 'Active Partners', value: '2,500+' },
                    { label: 'States Present', value: '15+' },
                    { label: 'Products', value: '25+' }
                ]
            },
            about: {
                title: 'About Power Clean India',
                description: 'Leading the way in premium cleaning solutions since 2024.',
                mission: 'Our mission is to empower every household with quality products and financial independence.',
                goal: 'Reach 1 Million households by 2030.',
                eco: 'Committed to 100% biodegradable packaging.',
                team: [
                    { id: 1, name: 'S Rajesh', role: 'Founder & CEO', image: 'https://i.pravatar.cc/150?u=1', bio: 'Visionary leader with 10+ years in MLM.' }
                ]
            },
            incomePlan: {
                title: 'Business Opportunity',
                description: 'Explore the various ways you can earn with Power Clean India, from retail profit to leadership bonuses.'
            },
            contact: {
                phone: '+91 98765-43210',
                email: 'support@powercleanindia.in',
                officeHours: 'Mon - Sat: 10:00 AM - 6:00 PM',
                address: '123 Business Hub, MG Road, New Delhi, India'
            }
        };
        try {
            const saved = localStorage.getItem('pci_content');
            if (saved) {
                const parsed = JSON.parse(saved);
                return { ...defaults, ...parsed };
            }
            return defaults;
        } catch (e) { return defaults; }
    });

    // --- FIREBASE SYNC (Replace LocalStorage) ---
    const [isFirebaseReady, setIsFirebaseReady] = useState(false);

    useEffect(() => {
        console.log("🔥 Connecting to Firebase...");

        // 1. Sync Settings
        const unsubSettings = onSnapshot(doc(db, "global", "settings"), (docSnap) => {
            if (docSnap.exists()) {
                const cloudData = docSnap.data();
                setSettings(prev => ({
                    ...prev,
                    ...cloudData,
                    // Force ensure critical arrays/values exist if cloud data is partial/corrupt
                    commissionLevels: cloudData.commissionLevels || prev.commissionLevels,
                    selfPurchaseBonus: cloudData.selfPurchaseBonus !== undefined ? cloudData.selfPurchaseBonus : prev.selfPurchaseBonus
                }));
            }
        });

        // 2. Sync Users
        // 2. Sync Users
        const unsubUsers = onSnapshot(collection(db, "users"), (snapshot) => {
            console.log(`🔥 FIRESTORE UPDATE: Found ${snapshot.docs.length} users.`);
            const list = snapshot.docs.map(d => {
                const data = d.data();
                return { ...data, id: data.id || d.id, firestoreId: d.id };
            });

            // Check if Admin exists in the cloud list
            const adminExists = list.some(u => String(u.id).toUpperCase() === 'PC1001');

            if (!adminExists) {
                console.warn("⚠️ Admin PC1001 missing from Firestore. Restoring...");
                // ... restore logic ...
                const defaultAdmin = {
                    id: 'PC1001', name: 'Company Manager', role: 'admin', email: 'admin@powercleanindia.in',
                    password: 'admin', wallet: 0, totalEarned: 0, status: 'active',
                    mobile: '9876543210', joinDate: new Date().toISOString()
                };
                setDoc(doc(db, "users", "PC1001"), defaultAdmin);
            }

            // --- Aggressive Session Sync ---
            // Ensure local session objects stay updated when Firestore data changes
            const savedAdminId = JSON.parse(localStorage.getItem('pci_admin_user') || 'null')?.id;
            const savedMemberId = JSON.parse(localStorage.getItem('pci_member_user') || 'null')?.id;

            if (savedAdminId) {
                const updated = list.find(u => {
                    const uId = String(u.id || '').toUpperCase();
                    const sId = String(savedAdminId).toUpperCase();
                    return uId === sId || (uId.startsWith('PC') && uId.slice(2) === sId) || (sId.startsWith('PC') && sId.slice(2) === uId);
                });
                if (updated) {
                    setAdminUser(updated);
                    localStorage.setItem('pci_admin_user', JSON.stringify(updated));
                } else {
                    // AUTO-LOGOUT if Admin ID no longer exists in DB
                    console.warn("⚠️ Admin Session Invalid (User Deleted). Logging out.");
                    setAdminUser(null);
                    localStorage.removeItem('pci_admin_user');
                }
            }
            if (savedMemberId) {
                const updated = list.find(u => {
                    const uId = String(u.id || '').toUpperCase();
                    const sId = String(savedMemberId).toUpperCase();
                    return uId === sId || (uId.startsWith('PC') && uId.slice(2) === sId) || (sId.startsWith('PC') && sId.slice(2) === uId);
                });
                if (updated) {
                    setMemberUser(updated);
                    localStorage.setItem('pci_member_user', JSON.stringify(updated));
                } else {
                    // AUTO-LOGOUT if Member ID no longer exists in DB
                    console.warn("⚠️ Member Session Invalid (User Deleted). Logging out.");
                    setMemberUser(null);
                    localStorage.removeItem('pci_member_user');
                }
            }

            setUsers(list);
        }, (error) => {
            console.error("🚨 FIRESTORE USER SYNC ERROR:", error);
            alert("Error syncing users: " + error.message);
        });

        // 3. Sync Products
        const unsubProducts = onSnapshot(collection(db, "products"), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setProducts(list.sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0)));
        });

        // 4. Sync Orders
        const unsubOrders = onSnapshot(query(collection(db, "orders"), orderBy("date", "desc")), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), id: d.id, firestoreId: d.id }));
            setOrders(list);
        });

        // 5. Sync Kits
        const unsubKits = onSnapshot(collection(db, "kits"), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setKits(list);
        });

        // 6. Sync Payouts
        const unsubPayouts = onSnapshot(collection(db, "payouts"), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), id: d.id, firestoreId: d.id }));
            setPayouts(list);
        });

        // 7. Sync Commission Logs
        const unsubCommissionLogs = onSnapshot(query(collection(db, "commissionLogs"), orderBy("date", "desc"), limit(2000)), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setCommissionLogs(list);
        });

        // 8. Sync Assembly Logs
        const unsubAssemblyLogs = onSnapshot(query(collection(db, "assemblyLogs"), orderBy("date", "desc"), limit(2000)), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setAssemblyLogs(list);
        });

        // 9. Sync Legal Docs
        const unsubLegal = onSnapshot(collection(db, "legal"), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setLegalDocs(list);
        });

        // 10. Sync Messages
        const unsubMessages = onSnapshot(query(collection(db, "messages"), orderBy("date", "desc"), limit(50)), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setContactMessages(list);
        });

        // 11. Sync Site Content (Critical for Mobile/PC uniformity)
        const unsubContent = onSnapshot(doc(db, "global", "content"), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setSiteContent(prev => ({ ...prev, ...data }));
                localStorage.setItem('pci_content', JSON.stringify(data));
            }
        });

        // 12. Sync Stock Logs (New)
        const unsubStockLogs = onSnapshot(query(collection(db, "stockLogs"), orderBy("date", "desc"), limit(200)), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setStockLogs(list);
        });

        // 13. Sync Expenses (New)
        const unsubExpenses = onSnapshot(query(collection(db, "expenses"), orderBy("date", "desc"), limit(100)), (snapshot) => {
            const list = snapshot.docs.map(d => ({ ...d.data(), firestoreId: d.id }));
            setExpenses(list);
        });

        setIsFirebaseReady(true);

        return () => {
            unsubSettings();
            unsubUsers();
            unsubProducts();
            unsubOrders();
            unsubKits();
            unsubPayouts();
            unsubCommissionLogs();
            unsubAssemblyLogs();
            unsubLegal();
            unsubMessages();
            unsubContent();
            unsubStockLogs();
            unsubExpenses();
        };
    }, []);

    // --- Session Persistence (Local) ---
    useEffect(() => {
        const admin = localStorage.getItem('pci_admin_user');
        const member = localStorage.getItem('pci_member_user');
        if (admin) {
            try { setAdminUser(JSON.parse(admin)); } catch (e) { localStorage.removeItem('pci_admin_user'); }
        }
        if (member) {
            try { setMemberUser(JSON.parse(member)); } catch (e) { localStorage.removeItem('pci_member_user'); }
        }
    }, []);





    const logStockMovement = async (itemType, itemId, itemName, qtyChange, reason, byUser = 'System', purchasePrice = 0, purchaseGst = 0) => {
        try {
            await addDoc(collection(db, "stockLogs"), {
                id: Date.now() + Math.random(),
                date: new Date().toISOString(),
                itemType, // 'product' or 'kit'
                itemId: String(itemId),
                itemName,
                quantity: Number(qtyChange),
                action: Number(qtyChange) > 0 ? 'Added' : 'Deducted',
                reason, // 'Manual Adjustment', 'Sale', 'Assembly', 'Return', 'Correction'
                purchasePrice: Number(purchasePrice || 0), // Total Purchase Cost (Excl GST)
                purchaseGst: Number(purchaseGst || 0),     // Total Input GST Paid
                performedBy: byUser
            });
        } catch (e) {
            console.error("Failed to log stock movement:", e);
        }
    };

    // --- SELF-HEALING: Fix Corrupted Admin Node (Infinite Loop Fix) ---
    // --- SELF-HEALING: Fix Corrupted Admin Node (Infinite Loop Fix) ---
    useEffect(() => {
        if (users.length > 0) {
            // Find Admin relative to Case Insensitivity
            const admin = users.find(u => String(u.id).toUpperCase() === 'PC1001');

            if (admin) {
                // Check if Admin has a sponsor (Loop) OR if Admin references ITSELF (Direct Loop)
                // Also check if ID string has spaces
                const needsRepair = admin.sponsorId || admin.leg || String(admin.id) !== 'PC1001';
                // We strictly enforce 'PC1001' uppercase as the canonical ID

                if (needsRepair) {
                    console.log(`🚨 REPAIRING ADMIN NODE: ${admin.id} => PC1001 (Removing Sponsor/Leg)`);

                    // 1. If ID is wrong case, we might need to recreate/move? 
                    // Firestore doc IDs are immutable. If doc ID is 'pc1001', we can't rename easily.
                    // For now, let's just fix the sponsorId on whatever doc exists.

                    if (admin.sponsorId || admin.leg) {
                        // The doc ID is stored in 'firestoreId' usually if we mapped it, 
                        // but here 'users' state comes from d.data(). 
                        // We need the ACTUAL document ID.
                        // In 'unsubUsers', we don't map firestoreId explicitly but usually id IS the key.

                        const docId = admin.firestoreId || admin.id; // Try both

                        updateDoc(doc(db, 'users', docId), {
                            sponsorId: null,
                            leg: null,
                            id: 'PC1001' // Enforce Uppercase in data field at least
                        }).then(() => console.log("✅ Admin Data Repaired")).catch(e => console.error(e));
                    }
                }
            }
        }
    }, [users]);

    // --- DATA SANITATION (Run on Mount) ---
    useEffect(() => {
        // ... (Sanitation code remains same, omitted for brevity if not changed, but must perform replace carefully)
        // Wait, replace tool needs contiguous block. I should only replace the snapshot part if I can't reach deleteKit in one go.
        // I will do two replaces. return to previous plan.
    }, []);

    // ...



    const syncAllKitPrices = async () => {
        try {
            const updates = kits.map(kit => {
                const baseSum = kit.contents?.reduce((sum, item) => {
                    const prod = products.find(p => String(p.id) === String(item.productId));
                    return sum + ((prod?.price || 0) * (item.qty || 1));
                }, 0) || 0;

                const gst = Number(kit.gst || 0);
                const discount = Number(kit.discount || 0);
                const finalPrice = Math.round(baseSum + (baseSum * (gst / 100)) - (baseSum * (discount / 100)));

                if (finalPrice !== kit.price && kit.firestoreId) {
                    return updateDoc(doc(db, "kits", kit.firestoreId), { price: finalPrice });
                }
                return null;
            }).filter(p => p !== null);

            await Promise.all(updates);
            return { success: true };
        } catch (e) {
            return { success: false, message: e.message };
        }
    };











    // 3. Current Sessions
    const [adminUser, setAdminUser] = useState(() => {
        try {
            // Strict Session Check: If this tab didn't start the session, ignore localStorage?
            // NO. If I reload the page, I should still be logged in. 
            // The "owned sessions" logic handles "is this MY session".
            // So we can check sessionStorage for owned ID.

            const saved = localStorage.getItem('pci_admin_user');
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    ...parsed,
                    wallet: Number(Number(parsed.wallet || 0).toFixed(2)),
                    totalEarned: Number(Number(parsed.totalEarned || 0).toFixed(2))
                };
            }
            return null;
        } catch (e) { return null; }
    });

    const [memberUser, setMemberUser] = useState(() => {
        try {
            // Strict Session Check
            const ownedSessions = JSON.parse(sessionStorage.getItem('pci_owned_sessions') || '{}');

            const saved = localStorage.getItem('pci_member_user');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Only restore if we own the session or if we are permissive (let's be permissive on reload, allow resume)
                // But wait, if I reload, I want to stay logged in.

                return {
                    ...parsed,
                    wallet: Number(Number(parsed.wallet || 0).toFixed(2)),
                    totalEarned: Number(Number(parsed.totalEarned || 0).toFixed(2))
                };
            }
            return null;
        } catch (e) { return null; }
    });



    const localSessionToken = useRef(null);
    const localUserId = useRef(null);

    // Reactive Status Check (Logout if blocked in DB)
    useEffect(() => {
        if (!users || users.length === 0) return;

        if (adminUser) {
            const currentAdmin = users.find(u => u.id === adminUser.id);
            if (currentAdmin && currentAdmin.status === 'blocked') {
                console.warn("Admin account blocked in DB. Logging out.");
                setAdminUser(null);
                localStorage.removeItem('pci_admin_user');
            }
        }

        if (memberUser) {
            const currentMember = users.find(u => u.id === memberUser.id);
            if (currentMember && currentMember.status === 'blocked') {
                console.warn("Member account blocked in DB. Logging out.");
                setMemberUser(null);
                localStorage.removeItem('pci_member_user');
            }
        }
    }, [users, adminUser, memberUser]);

    // Initialize local session token from storage on mount
    // Initialize local session token from storage on mount
    // Not needed anymore as we use sessionStorage 'pci_owned_sessions' map
    useEffect(() => {
        // No-op or cleanup legacy
    }, []);



    // ------------------------------------------------------------------
    // 4. Session Sync Effect (New)
    // ------------------------------------------------------------------
    useEffect(() => {
        if (users.length === 0) return;

        // Sync Admin
        if (adminUser) {
            const freshAdmin = users.find(u => u.id === adminUser.id);
            if (freshAdmin) {
                // If cloud data is different from local session, update session
                if (JSON.stringify(freshAdmin) !== JSON.stringify(adminUser)) {
                    console.log('Syncing Admin Session with Cloud Data...');
                    setAdminUser(freshAdmin);
                    localStorage.setItem('pci_admin_user', JSON.stringify(freshAdmin));
                }
            }
        }

        // Sync Member
        if (memberUser) {
            const freshMember = users.find(u => u.id === memberUser.id);
            if (freshMember) {
                // Check specific fields that might change (Wallet, Photo, Status, Name, Email, Phone, Bank, Nominee, Password)
                const hasChanged =
                    freshMember.profileImage !== memberUser.profileImage ||
                    freshMember.name !== memberUser.name ||
                    freshMember.email !== memberUser.email ||
                    freshMember.phone !== memberUser.phone ||
                    freshMember.mobile !== memberUser.mobile ||
                    freshMember.password !== memberUser.password ||
                    freshMember.wallet !== memberUser.wallet ||
                    freshMember.totalEarned !== memberUser.totalEarned ||
                    JSON.stringify(freshMember.bankDetails) !== JSON.stringify(memberUser.bankDetails) ||
                    JSON.stringify(freshMember.nominee) !== JSON.stringify(memberUser.nominee);

                if (hasChanged) {
                    console.log('Syncing Member Session with Cloud Data...');
                    setMemberUser(freshMember);
                    localStorage.setItem('pci_member_user', JSON.stringify(freshMember));
                }
            }
        }
    }, [users, adminUser, memberUser]);

    const updateSiteContent = async (newContent) => {
        try {
            // Optimistic update locally
            setSiteContent(newContent);
            localStorage.setItem('pci_content', JSON.stringify(newContent));

            // Persist to Firestore
            await setDoc(doc(db, "global", "content"), newContent);
            return { success: true };
        } catch (error) {
            console.error("Failed to update site content:", error);
            return { success: false, message: error.message };
        }
    };



    const addLegalDoc = async (docData) => {
        await addDoc(collection(db, "legal"), docData);
        return { success: true };
    };

    const deleteLegalDoc = async (id) => {
        const docObj = legalDocs.find(d => d.id === id);
        if (docObj?.firestoreId) {
            await deleteDoc(doc(db, "legal", docObj.firestoreId));
        }
        return { success: true };
    };

    const addContactMessage = async (msg) => {
        await addDoc(collection(db, "messages"), {
            ...msg, id: Date.now(), date: new Date().toISOString(), read: false
        });
        return { success: true };
    };

    const deleteContactMessage = async (id) => {
        try {
            const msg = contactMessages.find(m => m.id === id);
            if (msg && msg.firestoreId) {
                await deleteDoc(doc(db, "messages", msg.firestoreId));
            }
        } catch (e) { console.error("Error deleting message:", e); }
    };

    // --- EXPENSES (New) ---
    const addExpense = async (data) => {
        try {
            await addDoc(collection(db, "expenses"), {
                ...data,
                id: Date.now(),
                date: data.date || new Date().toISOString()
            });
            return { success: true };
        } catch (e) {
            console.error("Add Expense Failed:", e);
            return { success: false, message: e.message };
        }
    };

    const deleteExpense = async (id) => {
        try {
            const exp = expenses.find(e => e.id === id || e.firestoreId === id);
            if (exp && exp.firestoreId) {
                await deleteDoc(doc(db, "expenses", exp.firestoreId));
                return { success: true };
            }
            return { success: false, message: "Expense not found" };
        } catch (e) {
            console.error("Delete Expense Failed:", e);
            return { success: false, message: e.message };
        }
    };

    const editExpense = async (id, updatedData) => {
        try {
            const exp = expenses.find(e => e.id === id || e.firestoreId === id);
            if (exp && exp.firestoreId) {
                await updateDoc(doc(db, "expenses", exp.firestoreId), {
                    ...updatedData,
                    amount: Number(updatedData.amount) // Ensure amount is number
                });
                return { success: true };
            }
            return { success: false, message: "Expense not found" };
        } catch (e) {
            console.error("Edit Expense Failed:", e);
            return { success: false, message: e.message };
        }
    };

    // Persistence Effects
    useEffect(() => {
        if (adminUser) {
            localStorage.setItem('pci_admin_user', JSON.stringify(adminUser));
        } else {
            // Only remove from storage if the session being cleared is the one this tab owned
            const saved = localStorage.getItem('pci_admin_user');
            if (saved) {
                try {
                    // Same logic as member
                } catch (e) { localStorage.removeItem('pci_admin_user'); }
            }
        }
    }, [adminUser]);

    useEffect(() => {
        if (memberUser) {
            localStorage.setItem('pci_member_user', JSON.stringify(memberUser));
        } else {
            // Only remove from storage if the session being cleared is the one this tab owned
            const saved = localStorage.getItem('pci_member_user');
            if (saved) {
                try {
                    const parsed = JSON.parse(saved);
                    // If the user in storage matches the one we just logged out, remove it.
                    // Logic: If memberUser becomes null, it means WE logged out.
                    // But wait, if another tab logged out, memberUser becomes null too via storage sync.
                    // So purely, if memberUser is null, we should ensure storage is clear?
                    // NO. Storage sync handles the cleaning.
                    // If we set memberUser(null), we should clear storage.
                } catch (e) { localStorage.removeItem('pci_member_user'); }
            }
        }
    }, [memberUser]);

    // Persistence Effects (REMOVED LocalStorage sync for data)

    // --- Manual Migration Tool ---
    // Auto-migration removed to prevent infinite loops and give user control.
    // Migration can be triggered manually from the Settings page.

    // Cross-tab Synchronization
    useEffect(() => {
        const handleSync = (e) => {
            if (!e.newValue) {
                // Handle deletion/logout from other tabs
                // Only clear if we actually have a user of that role in this tab
                if (e.key === 'pci_admin_user' && adminUser) {
                    // Check if value is truly gone or just changed
                    // But here it means newValue is null
                    // We only auto-logout if the SESSION ID matches what we thought we had, OR if we want to sync logouts.
                    // Actually, if I logged out in Tab A, Tab B (same user) should also logout?
                    // YES.
                    setAdminUser(null);
                }
                if (e.key === 'pci_member_user' && memberUser) {
                    setMemberUser(null);
                }
                return;
            }
            try {
                const data = JSON.parse(e.newValue);
                if (e.key === 'pci_orders') setOrders(data);
                if (e.key === 'pci_users') setUsers(data);
                if (e.key === 'pci_settings') setSettings(data);
                if (e.key === 'pci_admin_user') {
                    // Only sync if it's a logout (data is null, handled above)
                    // or if we are already logged in as this person (might be a wallet update)
                    if (adminUser?.id === data?.id) setAdminUser(data);
                }
                if (e.key === 'pci_member_user') {
                    if (memberUser?.id === data?.id) setMemberUser(data);
                }
                if (e.key === 'pci_commission_logs') setCommissionLogs(data);
                if (e.key === 'pci_payouts') setPayouts(data);
                if (e.key === 'pci_content') setSiteContent(data);
                if (e.key === 'pci_legal') setLegalDocs(data);
                if (e.key === 'pci_messages') setContactMessages(data);
                if (e.key === 'pci_products') setProducts(data);
                if (e.key === 'pci_kits') setKits(data);
                if (e.key === 'pci_assembly_logs') setAssemblyLogs(data);
            } catch (err) { console.error("Sync error:", err); }
        };
        window.addEventListener('storage', handleSync);
        return () => window.removeEventListener('storage', handleSync);
    }, [adminUser, memberUser]);

    // Session Mismatch Check (Reactive + Polling)
    // Session Mismatch Check (Reactive + Polling)
    useEffect(() => {
        const checkSession = () => {
            const ownedSessions = JSON.parse(sessionStorage.getItem('pci_owned_sessions') || '{}');

            // Check Admin - RELAXED for Multi-Tab Support
            if (adminUser) {
                const ownedSessionId = ownedSessions[adminUser.id];
                if (ownedSessionId) {
                    const latestUserState = users.find(u => u.id === adminUser.id);
                    if (latestUserState && latestUserState.sessionId && latestUserState.sessionId !== ownedSessionId) {
                        // console.warn(`Admin Session mismatch: Local ${ownedSessionId} vs Cloud ${latestUserState.sessionId} (Ignored for multi-tab support)`);
                        // Allow multiple sessions - DO NOT LOGOUT
                    }
                }
            }

            // Check Member - RELAXED for Multi-Tab Support
            if (memberUser) {
                const ownedSessionId = ownedSessions[memberUser.id];
                if (ownedSessionId) {
                    const latestUserState = users.find(u => u.id === memberUser.id);
                    if (latestUserState && latestUserState.sessionId && latestUserState.sessionId !== ownedSessionId) {
                        // console.warn(`Member Session mismatch: Local ${ownedSessionId} vs Cloud ${latestUserState.sessionId} (Ignored for multi-tab support)`);
                        // Allow multiple sessions - DO NOT LOGOUT
                    }
                }
            }
        };

        // Run check on users update
        checkSession();

        // Also Poll every 3 seconds for robustness
        const interval = setInterval(checkSession, 3000);
        return () => clearInterval(interval);
    }, [users, adminUser, memberUser]);

    // Session Sync with Users DB
    useEffect(() => {
        if (adminUser) {
            const latest = users.find(u => u.id === adminUser.id);
            if (latest && (latest.wallet !== adminUser.wallet || latest.totalEarned !== adminUser.totalEarned)) {
                setAdminUser(latest);
            }
        }
        if (memberUser) {
            const latest = users.find(u => u.id === memberUser.id);
            if (latest && (latest.wallet !== memberUser.wallet || latest.totalEarned !== memberUser.totalEarned)) {
                setMemberUser(latest);
            }
        }
    }, [users]);

    // Monthly Active Status Evaluation
    useEffect(() => {
        if (!users || users.length === 0) return;

        const currentMonthKey = new Date().toISOString().substring(0, 7); // e.g., "2023-10"

        // Only evaluate the logged-in member to prevent a single client from evaluating 
        // and writing back 5000 users at once when they open the app on the 1st
        if (memberUser && memberUser.status !== 'inactive') {
            const latest = users.find(u => u.id === memberUser.id);
            if (latest && latest.role !== 'admin' && latest.status === 'active') {
                // If they are strictly 'active' but they haven't bought a kit this month
                if (latest.lastKitPurchaseMonth !== currentMonthKey) {
                    console.log(`Monthly Reset: User ${latest.id} has not purchased a kit in ${currentMonthKey}. Marking inactive.`);
                    // Downgrade them to inactive visually and in DB
                    updateDoc(doc(db, "users", latest.firestoreId || latest.id), { status: 'inactive' })
                        .catch(err => console.error("Failed to auto-deactivate member:", err));

                    // Update local state temporarily to avoid waiting for snapshot
                    // Only do this if it actually changed, to prevent loops
                    if (memberUser.status !== 'inactive') {
                        setMemberUser(prev => ({ ...prev, status: 'inactive' }));
                    }
                }
            }
        }
    }, [users, memberUser]);

    // Inactivity Auto-Logout (15 Minutes)
    useEffect(() => {
        if (!adminUser && !memberUser) return;

        let lastActivity = Date.now();
        const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 Minutes

        const updateActivity = () => {
            lastActivity = Date.now();
        };

        // Events that count as activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        events.forEach(name => document.addEventListener(name, updateActivity));

        const interval = setInterval(() => {
            const now = Date.now();
            if (now - lastActivity > INACTIVITY_LIMIT) {
                console.warn("User inactive for 15 minutes. Logging out.");
                logout();
                alert("Session expired due to inactivity (15 mins). Please login again.");
            }
        }, 10000); // Check every 10 seconds

        return () => {
            events.forEach(name => document.removeEventListener(name, updateActivity));
            clearInterval(interval);
        };
    }, [adminUser, memberUser]);

    // Actions
    const login = async (identifier, password) => {
        const idLower = identifier.trim().toLowerCase();

        // Query Firestore directly for the most up-to-date session info
        let userToCheck = null;
        try {
            // First try looking by ID directly for efficiency
            const userRef = doc(db, 'users', identifier.trim().toUpperCase());
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const data = userSnap.data();
                if (data.password === password) {
                    userToCheck = { ...data, firestoreId: userSnap.id };
                }
            } else {
                // Try query by email/id lower if direct ID fetch failed (for email login)
                const q = query(
                    collection(db, "users"),
                    where("email", "==", idLower)
                );
                const querySnap = await getDocs(q);
                if (!querySnap.empty) {
                    const data = querySnap.docs[0].data();
                    if (data.password === password) {
                        userToCheck = { ...data, firestoreId: querySnap.docs[0].id };
                    }
                }
            }
        } catch (e) {
            console.error("Direct Firestore check failed", e);
        }

        // Emergency Admin Recovery: If PC1001 is missing from state (e.g., after reset), try fetching/creating it.
        if (!userToCheck && (idLower === 'pc1001' || idLower === 'admin@powercleanindia.in') && password === 'admin') {
            try {
                const adminRef = doc(db, 'users', 'PC1001');
                const adminSnap = await getDoc(adminRef);

                const defaultAdmin = {
                    id: 'PC1001', name: 'Company Manager', role: 'admin', email: 'admin@powercleanindia.in',
                    password: 'admin', wallet: 0, totalEarned: 0, status: 'active',
                    mobile: '9876543210', joinDate: new Date().toISOString()
                };

                if (!adminSnap.exists()) {
                    await setDoc(adminRef, defaultAdmin);
                    userToCheck = defaultAdmin;
                } else {
                    const data = adminSnap.data();
                    if (data.password === 'admin') {
                        userToCheck = data;
                    }
                }
            } catch (e) { console.error("Admin recovery failed", e); }
        }

        if (userToCheck) {
            // Check status for all users except master admin bypass
            if (userToCheck.status === 'blocked' && userToCheck.id !== 'PC1001') {
                return { success: false, message: 'Account is blocked. Please contact support.' };
            }

            // Re-check PC1001 status as well
            if (userToCheck.id === 'PC1001' && userToCheck.status === 'blocked') {
                return { success: false, message: 'Admin account is currently locked.' };
            }

            // override Session Logic: Always allow login
            // We generate a new SessionID which invalidates the old one.

            const sessionId = Date.now().toString() + Math.random().toString(36).substring(2);
            const userWithSession = { ...userToCheck, sessionId };

            // 1. Update Cloud (Critical for persistence)
            try {
                if (userToCheck.id) {
                    await updateDoc(doc(db, "users", userToCheck.id), {
                        sessionId,
                        lastActive: new Date().toISOString()
                    });
                }
            } catch (e) {
                console.error("Failed to persist session:", e);
                return { success: false, message: "Login failed: Connection error." };
            }

            // 2. Update Local State & Storage based on Role
            setUsers(prev => {
                const index = prev.findIndex(u => u.id === userToCheck.id);
                if (index !== -1) {
                    const newUsers = [...prev];
                    newUsers[index] = userWithSession;
                    return newUsers;
                } else {
                    return [...prev, userWithSession];
                }
            });

            if (userToCheck.role === 'admin') {
                setAdminUser(userWithSession);
                localStorage.setItem('pci_admin_user', JSON.stringify(userWithSession));
                // Do NOT clear memberUser
            } else {
                setMemberUser(userWithSession);
                localStorage.setItem('pci_member_user', JSON.stringify(userWithSession));
                // Do NOT clear adminUser
            }

            // Update refs for this specific role's current session
            // We need separate tracking for Admin and Member session IDs if we want strict per-role enforcement in same tab
            // But usually one tab = one "user context" in simpler apps. 
            // However, request is to allow Admin and Member IN DIFFERENT TABS.
            // So this `localSessionToken` approach needs to specific to the role we just logged in.

            // To simplify: We just rely on the user object's sessionId matching the cloud.
            // But we need to know if THIS tab owns the session.
            // We can store a map of owned session IDs in sessionStorage.

            const ownedSessions = JSON.parse(sessionStorage.getItem('pci_owned_sessions') || '{}');
            ownedSessions[userToCheck.id] = sessionId;
            sessionStorage.setItem('pci_owned_sessions', JSON.stringify(ownedSessions));

            return { success: true, role: userToCheck.role };
        }
        return { success: false, message: 'Invalid credentials' };
    };

    const logout = async (role) => {
        // If role is provided, only logout that role. 
        // If no role (generic logout), try to clear everything (or passed from nav).

        let targetId = null;

        if (role === 'admin' && adminUser) {
            targetId = adminUser.id;
            setAdminUser(null);
            localStorage.removeItem('pci_admin_user');
        } else if (role === 'member' && memberUser) {
            targetId = memberUser.id;
            setMemberUser(null);
            localStorage.removeItem('pci_member_user');
        } else {
            // Fallback: Clear both if unspecified (or call twice)
            if (adminUser) {
                await updateDoc(doc(db, "users", adminUser.id), { sessionId: null });
                setAdminUser(null);
                localStorage.removeItem('pci_admin_user');
            }
            if (memberUser) {
                await updateDoc(doc(db, "users", memberUser.id), { sessionId: null });
                setMemberUser(null);
                localStorage.removeItem('pci_member_user');
            }
            sessionStorage.removeItem('pci_owned_sessions');
            return;
        }

        if (targetId) {
            try {
                await updateDoc(doc(db, "users", targetId), {
                    sessionId: null,
                    lastActive: null
                });
            } catch (e) { console.error("Logout cleanup failed", e); }

            // Clear from owned sessions
            const ownedSessions = JSON.parse(sessionStorage.getItem('pci_owned_sessions') || '{}');
            delete ownedSessions[targetId];
            sessionStorage.setItem('pci_owned_sessions', JSON.stringify(ownedSessions));
        }
    };

    const registerUser = async (userData) => {
        const cleanEmail = userData.email?.trim().toLowerCase();
        const cleanSponsorId = userData.sponsorId?.trim().toUpperCase();
        const cleanMobile = userData.mobile?.trim() || userData.phone?.trim();

        if (settings?.registrationsEnabled === false) return { success: false, message: 'Registrations are currently disabled.' };
        if (users.some(u => u.email?.toLowerCase() === cleanEmail)) return { success: false, message: 'Email already registered.' };
        if (!cleanSponsorId || cleanSponsorId === 'UNDEFINED') return { success: false, message: 'Sponsor ID required.' };

        const sponsor = users.find(u => {
            const uid = String(u.id || '').toUpperCase();
            return uid === cleanSponsorId || (uid.startsWith('PC') && uid.slice(2) === cleanSponsorId) || (cleanSponsorId.startsWith('PC') && cleanSponsorId.slice(2) === uid);
        });
        if (!sponsor) return { success: false, message: `Sponsor ${cleanSponsorId} not found.` };

        const activeLegs = users.filter(u => String(u.sponsorId || '').toUpperCase() === String(sponsor.id || '').toUpperCase());
        if (activeLegs.length >= 5) return { success: false, message: `Sponsor ${sponsor.id} already has 5 direct members. Level 1 (5 legs) limit reached.` };

        try {
            return await runTransaction(db, async (transaction) => {
                const counterRef = doc(db, "global", "counters");
                const counterDoc = await transaction.get(counterRef);

                // 3. Robust ID Generation with Collision Check
                let nextNum = 1005; // Default start
                if (counterDoc.exists()) {
                    nextNum = (counterDoc.data().lastMemberId || 1004) + 1;
                }

                let newId = `PC${nextNum}`;

                // loop until we find a free ID (max 10 tries to be safe)
                let attempts = 0;
                while (attempts < 10) {
                    // Check if this ID is already taken in the "users" collection (we need to read it)
                    // Transactional read is best.
                    const checkRef = doc(db, "users", newId);
                    const checkSnap = await transaction.get(checkRef);

                    if (!checkSnap.exists() && newId !== 'PC1001') {
                        break; // Free ID found!
                    }

                    // If taken or is PC1001, increment and try again
                    nextNum++;
                    newId = `PC${nextNum}`;
                    attempts++;
                }

                // Final Safety Guard
                if (newId === 'PC1001') {
                    throw new Error("Critical Error: System attempted to overwrite Admin ID. Registration aborted.");
                }

                const newUser = {
                    ...userData, id: newId, email: cleanEmail, mobile: cleanMobile,
                    sponsorId: sponsor.id, role: 'member', wallet: 0, totalEarned: 0,
                    status: 'active', joinDate: new Date().toISOString()
                };

                transaction.set(doc(db, "users", newId), newUser);
                transaction.set(counterRef, { lastMemberId: nextNum }, { merge: true });

                return { success: true, user: newUser };
            });
        } catch (e) {
            console.error("Registration Transaction Failed:", e);
            let message = e.message;
            if (e.code === 'permission-denied' || message.toLowerCase().includes('permission')) {
                message = "Registration failed due to missing Firestore permissions. Please ensure your Firestore Security Rules allow public writes to the 'users' and 'global' collections, or contact the administrator.";
            }
            return { success: false, message };
        }
    };

    const updateSettings = async (newSettings) => {
        // setSettings(prev => ({ ...prev, ...newSettings }));
        await setDoc(doc(db, "global", "settings"), { ...settings, ...newSettings });
    };

    const processPayout = async (userId, amount, customTxnId = null) => {
        try {
            const user = users.find(u => u.id === userId);
            if (!user) return { success: false, message: 'User not found' };

            // Validation: Ensure amount is positive and within balance
            if (amount <= 0) return { success: false, message: 'Invalid payout amount' };
            if (amount > (Number(user.wallet) || 0)) return { success: false, message: 'Insufficient balance for this payout' };

            const payoutId = `PAY${Date.now()}`;
            const newPayout = {
                id: payoutId,
                userId,
                amount: Number(amount),
                date: new Date().toISOString(),
                status: 'Completed',
                transactionId: customTxnId || `TXN-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
            };

            await Promise.all([
                addDoc(collection(db, "payouts"), newPayout),
                updateDoc(doc(db, "users", userId), {
                    wallet: Number((Number(user.wallet || 0) - amount).toFixed(2))
                })
            ]);
            return { success: true };
        } catch (e) {
            console.error("Payout Processing Error:", e);
            return { success: false, message: e.message };
        }
    };

    const updatePayout = async (id, updates) => {
        // Since payouts might not have custom ID as document name, we'd normally query.
        // But if we use firestoreId from onSnapshot:
        const payout = payouts.find(p => p.id === id);
        if (payout?.firestoreId) {
            await updateDoc(doc(db, "payouts", payout.firestoreId), updates);
        }
        return { success: true };
    };


    const distributeCommissions = async (memberId, saleAmount, isSimulation = false) => {
        console.log(`--- Distributing Commission for ${memberId}, Sale: ${saleAmount} ---`);
        const updatesDict = {};
        const distributionLog = [];
        const amountNum = Number(saleAmount);
        if (isNaN(amountNum) || amountNum <= 0) {
            alert(`Error: Invalid sale amount "${saleAmount}"`);
            return { success: false, message: "Invalid sale amount" };
        }

        if (!users || users.length === 0) {
            alert("Error: User database not loaded yet.");
            return { success: false, message: "User database not loaded" };
        }

        const buyer = findUserById(memberId);
        if (!buyer) {
            const errorMsg = `Error: Member ID "${memberId}" not found in current database list.`;
            console.error(errorMsg);
            return { success: false, message: errorMsg };
        }

        if (!memberId || String(memberId).toUpperCase() === 'UNDEFINED') {
            return { success: false, message: "Invalid Member ID (undefined)" };
        }

        // Validate Settings for Commission Levels as per user requirement
        const levels = Array.isArray(settings?.commissionLevels) ? settings.commissionLevels : [5, 4, 3, 2, 1, 1, 0.5];
        const selfBonus = settings?.selfPurchaseBonus || 10;

        // DEBUG: Hierarchy Trace
        let traceId = buyer.id;
        let traceChain = [traceId];
        let tempUser = buyer;
        for (let i = 0; i < 10; i++) {
            if (!tempUser.sponsorId) break;
            const sprId = String(tempUser.sponsorId || '').trim().toUpperCase();
            if (!sprId || sprId === 'UNDEFINED') break;
            const spr = findUserById(sprId);
            if (!spr) {
                traceChain.push(`${sprId} (MISSING)`);
                break;
            }
            const safeSprId = spr.id || (spr.firestoreId ? `FS:${spr.firestoreId}` : 'ID_UNKNOWN');
            traceChain.push(safeSprId);
            tempUser = spr;
            if (safeSprId === 'PC1001') break;
        }
        console.log(`Commission Hierarchy Trace:\nBuyer: ${buyer.name} (${buyer.id})\nChain: ${traceChain.join(' -> ')}`);

        if (selfBonus > 0) {
            const bonus = Number(((amountNum * selfBonus) / 100).toFixed(2));
            updatesDict[buyer.id] = (updatesDict[buyer.id] || 0) + bonus;
            distributionLog.push({ level: 0, sponsorId: buyer.id, amount: bonus, percentage: selfBonus, note: 'Self Bonus' });
            console.log(`Self Bonus: ${buyer.id} gets ₹${bonus}`);
        }

        let currentRecipientId = buyer.sponsorId;
        console.log(`Initial Sponsor: ${currentRecipientId}`);

        const maxLevels = levels.length;
        for (let level = 0; level < maxLevels; level++) {
            if (!currentRecipientId) {
                console.log(`Chain ended at level ${level} (no more sponsors).`);
                break;
            }

            const recipient = findUserById(currentRecipientId);
            if (!recipient) {
                const msg = `Sponsor ID "${currentRecipientId}" not found in database at Level ${level + 1}.`;
                console.warn(msg);
                distributionLog.push({ level: level + 1, sponsorId: currentRecipientId, amount: 0, percentage: 0, note: 'Sponsor Not Found' });
                // Don't break immediately, maybe something else is wrong? 
                // Actually, if sponsor is missing, chain must break because we don't know who is above them.
                break;
            }

            const percentage = Number(levels[level] || 0);
            const commission = Number(((amountNum * percentage) / 100).toFixed(2));

            if (commission > 0) {
                const uid = recipient.id; // Use consistent key
                updatesDict[uid] = (updatesDict[uid] || 0) + commission;
                distributionLog.push({ level: level + 1, sponsorId: uid, amount: commission, percentage, note: `Level ${level + 1}` });
                console.log(`Level ${level + 1}: ${uid} gets ₹${commission} (${percentage}%)`);
            }

            currentRecipientId = recipient.sponsorId;
        }

        if (isSimulation) return { success: true, log: distributionLog, message: "Simulation success" };

        const confirmMsg = `Recipients Found:\n${Object.entries(updatesDict).map(([u, a]) => `${u}: ₹${a}`).join('\n')}\n\nProceed to update wallets?`;
        console.log(confirmMsg);

        if (Object.keys(updatesDict).length > 0) {
            // Write to Cloud
            try {
                const batchPromises = Object.entries(updatesDict).map(([uid, amt]) => {
                    const user = findUserById(uid);
                    if (!user) {
                        console.error(`User ${uid} not found during batch building!`);
                        return Promise.resolve();
                    }
                    const docId = user.firestoreId || user.id;
                    const walletAmt = Number(amt);
                    return updateDoc(doc(db, "users", docId), {
                        wallet: increment(walletAmt),
                        totalEarned: increment(walletAmt)
                    });
                });

                const logPromises = distributionLog.map(log => {
                    return addDoc(collection(db, "commissionLogs"), {
                        ...log, id: Date.now() + Math.random(), date: new Date().toISOString(), sourceMemberId: memberId
                    });
                });

                await Promise.all([...batchPromises, ...logPromises]);
            } catch (error) {
                console.error("Commission Distribution Failed:", error);
                return { success: false, message: "Database write failed: " + error.message };
            }
        }

        const totalDistributed = Object.values(updatesDict).reduce((a, b) => a + b, 0);
        const chainStr = distributionLog.filter(l => l.amount > 0).map(l => `${l.note}: ${l.sponsorId} (₹${l.amount})`).join('\n');

        if (totalDistributed === 0) {
            console.log(`Trace result: No commission distributed.\nBuyer: ${buyer.id}\nSponsor: ${buyer.sponsorId}\nReason: No active sponsors or 0% rates.`);
            return { success: true, log: distributionLog, message: "No commission distributed." };
        }

        const successMsg = `Successfully distributed ₹${totalDistributed.toLocaleString()} to ${Object.keys(updatesDict).length} members.\n\nChain Trace:\n${chainStr}`;
        console.log(successMsg);
        return { success: true, log: distributionLog, message: successMsg };
    };

    const updateUserStatus = async (userId, status) => {
        try {
            if (!userId) return { success: false, message: "User ID is required" };

            const normalizedId = String(userId).trim().toUpperCase();
            const user = users.find(u => String(u.id).trim().toUpperCase() === normalizedId);

            // Critical: Use firestoreId if available, fallback to normalized ID
            const docId = user?.firestoreId || normalizedId;

            const updates = { status };
            if (status === 'active') {
                updates.lastKitPurchaseMonth = new Date().toISOString().substring(0, 7);
            }

            await updateDoc(doc(db, "users", docId), updates);
            console.log(`✅ Status updated for ${normalizedId}: ${status}`);
            return { success: true };
        } catch (e) {
            console.error("Status update failed:", e);
            return { success: false, message: e.message };
        }
    };

    const updateUser = async (id, updates) => {
        try {
            const user = users.find(u => String(u.id).toUpperCase() === String(id).toUpperCase());
            const docId = user?.firestoreId || id;
            await updateDoc(doc(db, "users", docId), updates);
            return { success: true };
        } catch (e) {
            console.error("User update failed:", e);
            return { success: false, message: e.message };
        }
    };

    const deleteUser = async (userId) => {
        try {
            const user = users.find(u => String(u.id).toUpperCase() === String(userId).toUpperCase());
            const docId = user?.firestoreId || userId;
            await deleteDoc(doc(db, "users", docId));
            return { success: true };
        } catch (e) {
            console.error("Delete user failed:", e);
            return { success: false, message: e.message };
        }
    };

    const addProduct = async (p) => {
        const newStock = Number(p.stock) || 0;
        const generatedId = Date.now();
        // Sanitize Cost Price (Fix 2.25 Bug)
        const safeCostPrice = (Number(p.costPrice) === 2.25) ? 0 : Number(p.costPrice || 0);
        const purchasePrice = safeCostPrice * newStock;
        const purchaseGst = (purchasePrice * (Number(p.gst || 0) / 100));

        await addDoc(collection(db, "products"), { ...p, id: generatedId, stock: newStock, costPrice: safeCostPrice });

        if (newStock > 0) {
            // Log Stock Movement with Purchase Price & GST for P/L Report
            logStockMovement('product', generatedId, p.name, newStock, 'Initial Stock', currentUser?.name || 'Admin', purchasePrice, purchaseGst);
        }
    };

    const updateProduct = async (id, u, options = {}) => {
        console.log(`🛠️ updateProduct(${id}):`, u);
        const prod = products.find(p => p.id === id);
        if (prod?.firestoreId) {
            // Sanitize Update Data (Fix 2.25 Bug)
            const safeUpdates = { ...u };
            if (u.costPrice !== undefined) {
                safeUpdates.costPrice = (Number(u.costPrice) === 2.25) ? 0 : Number(u.costPrice);
            }

            await updateDoc(doc(db, "products", prod.firestoreId), safeUpdates);

            // Log Stock Change if applicable
            if (u.stock !== undefined && !options.skipLog) {
                const oldStock = Number(prod.stock || 0);
                const newStock = Number(u.stock);
                const diff = newStock - oldStock;
                if (diff !== 0) {
                    let purchasePrice = 0;
                    let purchaseGst = 0;

                    // Calculate Investment if Stock is Added
                    if (diff > 0) {
                        const effectiveCost = Number(u.costPrice !== undefined ? u.costPrice : (prod.costPrice || 0));
                        const effectiveGst = Number(u.gst !== undefined ? u.gst : (prod.gst || 0));
                        purchasePrice = diff * effectiveCost;
                        purchaseGst = purchasePrice * (effectiveGst / 100);
                    }

                    logStockMovement('product', prod.id, prod.name, diff, 'Manual Adjustment', (adminUser || memberUser)?.name || 'Admin', purchasePrice, purchaseGst);
                }
            }
        }
    };

    const deleteProduct = async (id) => {
        const prod = products.find(p => p.id === id);
        if (prod?.firestoreId) {
            await deleteDoc(doc(db, "products", prod.firestoreId));
            logStockMovement('product', prod.id, prod.name, -(prod.stock || 0), 'Product Deleted', currentUser?.name || 'Admin');
        }
    };

    const placeOrder = async (userId, items, total, paymentScreenshot = null) => {
        if (!userId) {
            alert("Error: User ID is missing. Please re-login.");
            return { success: false, message: "User ID missing" };
        }

        try {
            return await runTransaction(db, async (transaction) => {
                const counterRef = doc(db, "global", "counters");
                const counterDoc = await transaction.get(counterRef);

                let nextNum = 1;
                if (counterDoc.exists() && counterDoc.data().lastOrderId) {
                    nextNum = Number(counterDoc.data().lastOrderId) + 1;
                }

                // Format: PCI001, PCI010, PCI100 etc.
                const orderId = `PCI${String(nextNum).padStart(3, '0')}`;

                // Calculate Snapshots for Reports
                let totalGst = 0;
                let totalCost = 0;

                const snapshotItems = items.map(item => {
                    const originalItem = (item.type === 'kit' ? kits : products).find(x => String(x.id) === String(String(item.id).replace('kit-', '')));
                    const isKit = item.type === 'kit' || String(item.id).startsWith('kit-');
                    const gstPercent = isKit ? 0 : Number(originalItem?.gst || 0);
                    const discountPercent = Number(originalItem?.discount || 0);
                    const price = Number(item.price || 0);
                    const qty = Number(item.quantity || 1);
                    let costPrice = Number(originalItem?.costPrice || 0);

                    if (costPrice === 0 && originalItem && Array.isArray(originalItem.contents)) {
                        costPrice = originalItem.contents.reduce((sum, content) => {
                            const prod = products.find(p => String(p.id) === String(content.productId));
                            return sum + ((Number(prod?.costPrice) || 0) * (content.qty || 1));
                        }, 0);
                    }

                    const itemTotal = price * qty;
                    const basePrice = itemTotal / (1 + (gstPercent / 100));
                    const gstAmount = itemTotal - basePrice;

                    totalGst += gstAmount;
                    totalCost += (costPrice * qty);

                    return JSON.parse(JSON.stringify({
                        ...item,
                        gstPercent,
                        gstAmount: Number(gstAmount.toFixed(2)),
                        costPrice: Number(costPrice.toFixed(2)),
                        discountPercent
                    }));
                });

                const order = {
                    id: orderId,
                    userId,
                    items: snapshotItems,
                    totalAmount: Number(total),
                    gstTotal: Number(totalGst.toFixed(2)),
                    costTotal: Number(totalCost.toFixed(2)),
                    date: new Date().toISOString(),
                    status: 'Pending',
                    isCommissionPaid: false,
                    isStockDeducted: false,
                    paymentScreenshot
                };

                const safeOrder = JSON.parse(JSON.stringify(order));

                // Use orderId as the document ID for absolute consistency
                transaction.set(doc(db, "orders", orderId), safeOrder);
                transaction.update(counterRef, { lastOrderId: nextNum });

                return order;
            });

        } catch (e) {
            console.error("Critical: Order Placement System Error", e);
            alert("Order Failed: " + (e.message || "Unknown error"));
            return null;
        }
    };

    const revertCommissions = async (memberId, saleAmount) => {
        const updates = new Map();
        const reversalLog = [];
        const amountNum = Number(saleAmount);
        if (isNaN(amountNum) || amountNum <= 0) return;

        const buyer = users.find(u => u.id?.toUpperCase() === memberId?.toUpperCase());
        if (!buyer) return;

        // Revert Self Bonus
        if (settings.selfPurchaseBonus > 0) {
            const rawBonus = (amountNum * settings.selfPurchaseBonus) / 100;
            const bonus = Number(rawBonus.toFixed(2));
            updates.set(buyer.id, -bonus); // Negative value for deduction
            reversalLog.push({ level: 0, sponsorId: buyer.id, amount: -bonus, percentage: settings.selfPurchaseBonus, note: 'Reversal: Self Bonus' });
        }

        // Revert Upline Commissions
        let currentRecipientId = buyer.sponsorId;
        const levels = Array.isArray(settings?.commissionLevels) ? settings.commissionLevels : [5, 4, 3, 2, 1, 1, 0.5];
        const maxLevels = levels.length;
        for (let level = 0; level < maxLevels; level++) {
            const recipient = users.find(u => u.id?.toUpperCase() === currentRecipientId?.toUpperCase());
            if (!recipient) break;
            const percentage = levels[level] || 0;
            const rawCommission = (amountNum * percentage) / 100;
            const commission = Number(rawCommission.toFixed(2));
            if (commission > 0) {
                updates.set(recipient.id, (updates.get(recipient.id) || 0) - commission);
                reversalLog.push({ level: level + 1, sponsorId: recipient.id, amount: -commission, percentage, note: `Reversal: Level ${level + 1}` });
            }
            if (!recipient.sponsorId) break;
            currentRecipientId = recipient.sponsorId;
        }

        if (updates.size > 0) {
            try {
                // 1. Update Database (Atomic)
                const dbPromises = Array.from(updates.entries()).map(([uid, amt]) => {
                    const user = users.find(u => u.id === uid);
                    if (!user) return Promise.resolve();
                    const docId = user.firestoreId || user.id;
                    return updateDoc(doc(db, "users", docId), {
                        wallet: increment(amt),
                        totalEarned: increment(amt)
                    });
                });

                // 2. Add Logs
                const logPromises = reversalLog.map(log => {
                    return addDoc(collection(db, "commissionLogs"), {
                        ...log, id: Date.now() + Math.random(), date: new Date().toISOString(), sourceMemberId: memberId
                    });
                });

                await Promise.all([...dbPromises, ...logPromises]);
                console.log("Commissions reverted successfully in database.");
            } catch (err) {
                console.error("Failed to revert commissions in database:", err);
            }
        }
    };

    const deleteOrder = async (orderId) => {
        try {
            const order = orders.find(o => o.id === orderId);
            if (!order || !order.firestoreId) return { success: false, message: "Order not found" };
            await deleteDoc(doc(db, "orders", order.firestoreId));
            return { success: true };
        } catch (e) {
            console.error("Delete order failed:", e);
            return { success: false, message: e.message };
        }
    };

    const updateOrder = async (id, updates) => {
        const order = orders.find(o => o.id === id);
        if (!order || !order.firestoreId) return { success: false };

        // Handle Delivery (Commission Distribution + Stock Deduction)
        if (['Delivered', 'Completed', 'Return Rejected'].includes(updates.status)) {
            // 1. Commission Distribution
            if (!order.isCommissionPaid) {
                console.log(`Order ${id} marked as ${updates.status}. Attempting commission distribution for user ${order.userId}...`);
                const result = await distributeCommissions(order.userId, order.totalAmount);
                if (result.success) {
                    updates.isCommissionPaid = true;
                    console.log(`Commission distributed successfully for Order ${id}.`);
                } else {
                    console.error(`Commission distribution FAILED for Order ${id}:`, result.message);
                }
            }

            // 2. Stock Deduction (ONLY IF NOT ALREADY DEDUCTED)
            if (!order.isStockDeducted && order.items && order.items.length > 0) {
                console.log(`Order ${id} marked as ${updates.status}. Deducting stock...`);
                try {
                    for (const item of order.items) {
                        const isKit = item.type === 'kit' || String(item.id).startsWith('kit-');

                        // If order is delivered and contains a kit, make user active for the month
                        if (isKit && updates.status === 'Delivered') {
                            const currentMonthKey = new Date().toISOString().substring(0, 7); // e.g., "2023-10"
                            updateUser(order.userId, {
                                status: 'active',
                                lastKitPurchaseMonth: currentMonthKey
                            });
                            console.log(`User ${order.userId} activated based on Kit Delivery.`);
                        }

                        if (isKit) {
                            const kitId = String(item.id).replace('kit-', '');
                            const kit = kits.find(k => String(k.id) === kitId);
                            if (kit && kit.firestoreId) {
                                const qtySold = Number(item.quantity) || 0;
                                const availableKitStock = Number(kit.stock || 0);
                                let qtyToDeductFromKit = Math.min(qtySold, availableKitStock);
                                let remainingToDeductFromComponents = Math.max(0, qtySold - availableKitStock);

                                if (qtyToDeductFromKit > 0) {
                                    const newKitStock = Math.max(0, availableKitStock - qtyToDeductFromKit);
                                    await updateDoc(doc(db, "kits", kit.firestoreId), { stock: newKitStock });
                                }

                                let kitCost = Number(kit.costPrice || 0);
                                if (kitCost === 0 && kit.contents) {
                                    kitCost = kit.contents.reduce((sum, content) => {
                                        const prod = products.find(p => String(p.id) === String(content.productId));
                                        return sum + ((Number(prod?.costPrice) || 0) * Number(content.qty));
                                    }, 0);
                                }
                                const totalKitCOGS = kitCost * qtySold;
                                logStockMovement('kit', kit.id, kit.name, -qtySold, `Sale (Order ${order.id})`, order.userId, totalKitCOGS, 0);

                                if (remainingToDeductFromComponents > 0 && kit.contents) {
                                    for (const component of kit.contents) {
                                        const prod = products.find(p => String(p.id) === String(component.productId));
                                        if (prod && prod.firestoreId) {
                                            const deductQty = Number(component.qty) * remainingToDeductFromComponents;
                                            const currProdStock = Number(prod.stock) || 0;
                                            const newProdStock = Math.max(0, currProdStock - deductQty);
                                            await updateDoc(doc(db, "products", prod.firestoreId), { stock: newProdStock });
                                            const compUnitCost = Number(prod.costPrice || 0);
                                            const compTotalCOGS = compUnitCost * deductQty;
                                            logStockMovement('product', prod.id, prod.name, -deductQty, `Used in Kit (Sale, Order ${order.id})`, order.userId, compTotalCOGS, 0);
                                        }
                                    }
                                }
                            }
                        } else {
                            const prod = products.find(p => String(p.id) === String(item.id));
                            if (prod && prod.firestoreId) {
                                const qtySold = Number(item.quantity) || 0;
                                if (qtySold > 0) {
                                    const currentStock = Number(prod.stock) || 0;
                                    const newStock = Math.max(0, currentStock - qtySold);
                                    await updateDoc(doc(db, "products", prod.firestoreId), { stock: newStock });
                                    const unitCost = Number(prod.costPrice || 0);
                                    const totalCOGS = unitCost * qtySold;
                                    logStockMovement('product', prod.id, prod.name, -qtySold, `Sale (Order ${order.id})`, order.userId, totalCOGS, 0);
                                }
                            }
                        }
                    }
                    updates.isStockDeducted = true;
                    console.log(`Stock deducted successfully for Order ${id}.`);
                } catch (err) {
                    console.error(`Stock deduction FAILED for Order ${id}:`, err);
                }
            }
        } else {
            console.log(`DEBUG: Commission/Stock skip for Order ${id}. Status: ${updates.status}, CommissionPaid: ${order.isCommissionPaid}, StockDeducted: ${order.isStockDeducted}`);
        }

        // Handle Cancellation/Return (Commission Reversal + Stock Restoration)
        if (['Cancelled', 'Returned'].includes(updates.status)) {
            console.log(`Processing Return/Cancellation for Order ${id}`);

            // 1. Revert Commission if paid
            if (order.isCommissionPaid) {
                console.log("Reverting Commissions...");
                await revertCommissions(order.userId, order.totalAmount);
                updates.isCommissionPaid = false; // Mark as unpaid/reverted
            }

            // 2. Restore Stock ONLY if it was previously deducted
            if (order.isStockDeducted && order.items && order.items.length > 0) {
                console.log("Restoring Stock (was previously deducted)...");
                try {
                    const stockUpdates = [];
                    for (const item of order.items) {
                        const qtyToRestore = Number(item.quantity);
                        if (item.type === 'kit' || String(item.id).startsWith('kit-')) {
                            const kitId = String(item.id).replace('kit-', '');
                            const kit = kits.find(k => String(k.id) === kitId);
                            if (kit && kit.firestoreId) {
                                const newStock = (Number(kit.stock) || 0) + qtyToRestore;
                                stockUpdates.push(updateDoc(doc(db, "kits", kit.firestoreId), { stock: newStock }));
                                logStockMovement('kit', kit.id, kit.name, qtyToRestore, `Order ${updates.status} (Restoration)`, currentUser?.name);
                            }
                        } else {
                            const prod = products.find(p => String(p.id) === String(item.id));
                            if (prod && prod.firestoreId) {
                                const newStock = (Number(prod.stock) || 0) + qtyToRestore;
                                stockUpdates.push(updateDoc(doc(db, "products", prod.firestoreId), { stock: newStock }));
                                logStockMovement('product', prod.id, prod.name, qtyToRestore, `Order ${updates.status} (Restoration)`, currentUser?.name);
                            }
                        }
                    }
                    await Promise.all(stockUpdates);
                    updates.isStockDeducted = false;
                    console.log("Stock Restored Successfully.");
                } catch (err) {
                    console.error("Failed to restore stock:", err);
                }
            } else {
                console.log("Skipping Stock Restoration: Stock was never deducted.");
            }
        }

        await updateDoc(doc(db, "orders", order.firestoreId), updates);
        return { success: true };
    };

    const getDetailedBusinessStats = (memberId) => {
        const levels = Array.isArray(settings?.commissionLevels) ? settings.commissionLevels : [5, 4, 3, 2, 1, 1, 0.5];
        const stats = {
            0: { members: 1, sales: 0, returns: 0, commission: 0 }
        };
        // Build levels dynamically
        levels.forEach((_, i) => {
            stats[i + 1] = { members: 0, sales: 0, returns: 0, commission: 0 };
        });

        if (!memberId) return stats;

        // Level 0: Self
        orders.filter(o => {
            if (!memberId || !o.userId) return false;
            const mId = String(memberId).toUpperCase();
            const ouId = String(o.userId).toUpperCase();
            return mId === ouId || (mId.startsWith('PC') && mId.slice(2) === ouId) || (ouId.startsWith('PC') && ouId.slice(2) === mId);
        }).forEach(o => {
            if (['Delivered', 'Completed', 'Return Rejected', 'Return Requested'].includes(o.status)) {
                stats[0].sales += Number(o.totalAmount || 0);
            }
            if (o.status === 'Returned') {
                stats[0].returns += Number(o.totalAmount || 0);
            }
        });

        const levelsMap = new Map();
        const traverse = (pId, lvl) => {
            if (lvl > levels.length) return;
            users.filter(u => {
                const sId = String(u.sponsorId || '').toUpperCase();
                const parentId = String(pId).toUpperCase();
                return sId === parentId || (sId.startsWith('PC') && sId.slice(2) === parentId) || (parentId.startsWith('PC') && parentId.slice(2) === sId);
            }).forEach(c => {
                levelsMap.set(String(c.id).toUpperCase(), lvl);
                stats[lvl].members++;
                traverse(c.id, lvl + 1);
            });
        };
        traverse(memberId, 1);

        orders.forEach(o => {
            const orderUid = String(o.userId || '').toUpperCase();
            const l = levelsMap.get(orderUid) || levelsMap.get(orderUid.replace('PC', '')) || levelsMap.get(`PC${orderUid}`);

            if (l !== undefined) {
                if (['Delivered', 'Completed', 'Return Rejected', 'Return Requested'].includes(o.status)) {
                    stats[l].sales += Number(o.totalAmount || 0);
                }
                if (o.status === 'Returned') {
                    stats[l].returns += Number(o.totalAmount || 0);
                }
            }
        });

        commissionLogs.forEach(log => {
            if (String(log.sponsorId).toUpperCase() === String(memberId).toUpperCase()) {
                const l = Number(log.level);
                // Allow level 0
                if (l >= 0 && l <= levels.length) stats[l].commission += Number(log.amount || 0);
            }
        });
        return stats;
    };

    const getTeamStats = (id) => {
        let total = 0;
        const traverse = (pId, lvl) => {
            const maxLevels = settings?.commissionLevels?.length || 7;
            if (lvl > maxLevels) return;
            const children = users.filter(u => {
                const sId = String(u.sponsorId || '').toUpperCase();
                const parentId = String(pId).toUpperCase();
                return sId === parentId || (sId.startsWith('PC') && sId.slice(2) === parentId) || (parentId.startsWith('PC') && parentId.slice(2) === sId);
            });
            total += children.length;
            children.forEach(c => traverse(c.id, lvl + 1));
        };
        traverse(id, 1);
        return { total };
    };

    const refreshSystemData = () => {
        // Round Users (Wallet & Total Earned) to 2 decimals
        setUsers(prev => prev.map(u => ({
            ...u,
            wallet: Number(Number(u.wallet || 0).toFixed(2)),
            totalEarned: Number(Number(u.totalEarned || 0).toFixed(2))
        })));

        // Round Orders (Total Amount)
        setOrders(prev => prev.map(o => ({
            ...o,
            totalAmount: Number(Number(o.totalAmount || 0).toFixed(2))
        })));

        // Round Payouts (Amount)
        setPayouts(prev => prev.map(p => ({
            ...p,
            amount: Number(Number(p.amount || 0).toFixed(2))
        })));

        // Round Commission Logs (Amount)
        setCommissionLogs(prev => prev.map(c => ({
            ...c,
            amount: Number(Number(c.amount || 0).toFixed(2))
        })));



        return { success: true, message: "System Data Refreshed: All values rounded to 2 decimal places." };
    };

    const fixTreeHierarchy = async () => {
        let movedCount = 0;
        const targetSponsorId = 'PC1002';
        let currentLegs = users.filter(u => u.sponsorId?.toUpperCase() === targetSponsorId.toUpperCase()).length;
        const LIMIT = 5;

        const updates = users.map(u => {
            if (u.sponsorId === 'PC1001' && u.id !== 'PC1002' && currentLegs < LIMIT) {
                movedCount++;
                currentLegs++;
                return { id: u.id, sponsorId: targetSponsorId };
            }
            return null;
        }).filter(u => u !== null);

        if (updates.length > 0) {
            await Promise.all(updates.map(u => updateDoc(doc(db, "users", u.id), { sponsorId: u.sponsorId })));
            return { success: true, message: `Repaired Tree: Moved ${movedCount} members.` };
        }
        return { success: false, message: "No moves needed." };
    };

    const resetFinancialData = async () => {
        try {
            // 1. Clear Standard Collections
            const collectionsToClear = ["orders", "payouts", "commissionLogs", "assemblyLogs", "messages", "legal", "stockLogs", "expenses"];

            for (const colName of collectionsToClear) {
                const q = query(collection(db, colName));
                const snapshot = await getDocs(q);
                const batch = writeBatch(db);
                let counter = 0;

                for (const docSnap of snapshot.docs) {
                    batch.delete(docSnap.ref);
                    counter++;
                    if (counter >= 400) { // Batch limit is 500
                        await batch.commit();
                        counter = 0;
                    }
                }
                if (counter > 0) await batch.commit();
            }

            // 2. Clear Users (Except PC1001)
            const usersQ = query(collection(db, "users"));
            const usersSnap = await getDocs(usersQ);
            let userBatch = writeBatch(db);
            let userCount = 0;

            for (const docSnap of usersSnap.docs) {
                if (docSnap.id !== 'PC1001') {
                    userBatch.delete(docSnap.ref);
                } else {
                    // Reset Admin Wallet
                    userBatch.update(docSnap.ref, { wallet: 0, totalEarned: 0, totalSales: 0 });
                }
                userCount++;
                if (userCount >= 400) {
                    await userBatch.commit();
                    userBatch = writeBatch(db); // FIX: Must re-initialize
                    userCount = 0;
                }
            }
            if (userCount > 0) await userBatch.commit();

            // 3. Reset Kits Stock to 0
            const kitsSnap = await getDocs(collection(db, "kits"));
            let kitBatch = writeBatch(db);
            let kitCount = 0;
            for (const docSnap of kitsSnap.docs) {
                kitBatch.update(docSnap.ref, { stock: 0, stockSynced: false });
                kitCount++;
                if (kitCount >= 400) {
                    await kitBatch.commit();
                    kitBatch = writeBatch(db); // FIX: Must re-initialize
                    kitCount = 0;
                }
            }
            if (kitCount > 0) await kitBatch.commit();

            // 4. Reset Products Stock to 0 (Crucial for fresh start)
            const prodSnap = await getDocs(collection(db, "products"));
            let prodBatch = writeBatch(db);
            let prodCount = 0;
            for (const docSnap of prodSnap.docs) {
                prodBatch.update(docSnap.ref, { stock: 0 });
                prodCount++;
                if (prodCount >= 400) {
                    await prodBatch.commit();
                    prodBatch = writeBatch(db);
                    prodCount = 0;
                }
            }
            if (prodCount > 0) await prodBatch.commit();

            // 4. Reset Counters
            // Start at 1001 so next member is 1002. (PC1001 is Admin)
            await setDoc(doc(db, "global", "counters"), { lastMemberId: 1001 });

            return { success: true, message: "System Reset Complete. Only Admin (PC1001) remains." };
        } catch (error) {
            console.error("Master Reset Failed:", error);
            return { success: false, message: error.message };
        }
    };

    const resetCommissionLogic = async () => {
        try {
            // 1. Clear Financial & History Collections (Same as Master Reset but selective)
            const collectionsToClear = ["orders", "payouts", "commissionLogs", "assemblyLogs", "stockLogs", "expenses"];

            for (const colName of collectionsToClear) {
                const q = query(collection(db, colName));
                const snapshot = await getDocs(q);
                const batch = writeBatch(db);
                let counter = 0;

                for (const docSnap of snapshot.docs) {
                    batch.delete(docSnap.ref);
                    counter++;
                    if (counter >= 400) {
                        await batch.commit();
                        counter = 0;
                    }
                }
                if (counter > 0) await batch.commit();
            }

            // 2. Reset ALL Users Wallets (KEEPING ALL USERS)
            const usersSnap = await getDocs(collection(db, "users"));
            let userBatch = writeBatch(db);
            let userCount = 0;

            for (const docSnap of usersSnap.docs) {
                // Reset EVERY user's financial markers
                userBatch.update(docSnap.ref, {
                    wallet: 0,
                    totalEarned: 0,
                    totalSales: 0
                });
                userCount++;
                if (userCount >= 400) {
                    await userBatch.commit();
                    userBatch = writeBatch(db);
                    userCount = 0;
                }
            }
            if (userCount > 0) await userBatch.commit();

            return { success: true, message: "Selective Reset Complete. All balances and history cleared. Users and IDs are preserved." };
        } catch (error) {
            console.error("Selective Reset Failed:", error);
            return { success: false, message: error.message };
        }
    };

    const resetStockOnly = async () => {
        try {
            console.log("Starting Stock Reset...");

            // 1. Clear Logs (Stock & Assembly)
            const collectionsToClear = ["stockLogs", "assemblyLogs"];
            for (const colName of collectionsToClear) {
                const q = query(collection(db, colName));
                const snapshot = await getDocs(q);
                let batch = writeBatch(db);
                let opCount = 0;
                for (const docSnap of snapshot.docs) {
                    batch.delete(docSnap.ref);
                    opCount++;
                    if (opCount >= 400) {
                        await batch.commit();
                        batch = writeBatch(db); // FIX: Must re-initialize
                        opCount = 0;
                    }
                }
                if (opCount > 0) await batch.commit();
            }

            // 2. Reset Products Stock to 0
            const productsSnap = await getDocs(collection(db, "products"));
            let prodBatch = writeBatch(db);
            let prodCount = 0;
            for (const docSnap of productsSnap.docs) {
                prodBatch.update(docSnap.ref, { stock: 0 });
                prodCount++;
                if (prodCount >= 400) {
                    await prodBatch.commit();
                    prodBatch = writeBatch(db); // FIX: Must re-initialize
                    prodCount = 0;
                }
            }
            if (prodCount > 0) await prodBatch.commit();

            // 3. Reset Kits Stock to 0
            const kitsSnap = await getDocs(collection(db, "kits"));
            let kitBatch = writeBatch(db);
            let kitCount = 0;
            for (const docSnap of kitsSnap.docs) {
                kitBatch.update(docSnap.ref, { stock: 0, stockSynced: false });
                kitCount++;
                if (kitCount >= 400) {
                    await kitBatch.commit();
                    kitBatch = writeBatch(db); // FIX: Must re-initialize
                    kitCount = 0;
                }
            }
            if (kitCount > 0) await kitBatch.commit();

            return { success: true, message: "Stock Data Reset Successfully (Inventory set to 0). Orders/Users kept safe." };
        } catch (error) {
            console.error("Stock Reset Failed:", error);
            return { success: false, message: error.message };
        }
    };

    const resetAllCostPrices = async () => {
        try {
            console.log("Starting Bulk Cost Price Reset (0)...");

            // 1. Reset Products Cost Price to 0
            const productsSnap = await getDocs(collection(db, "products"));
            let prodBatch = writeBatch(db);
            let prodCount = 0;
            for (const docSnap of productsSnap.docs) {
                prodBatch.update(docSnap.ref, { costPrice: 0 });
                prodCount++;
                if (prodCount >= 400) {
                    await prodBatch.commit();
                    prodBatch = writeBatch(db);
                    prodCount = 0;
                }
            }
            if (prodCount > 0) await prodBatch.commit();

            // 2. Reset Kits Cost Price to 0
            const kitsSnap = await getDocs(collection(db, "kits"));
            let kitBatch = writeBatch(db);
            let kitCount = 0;
            for (const docSnap of kitsSnap.docs) {
                kitBatch.update(docSnap.ref, { costPrice: 0 });
                kitCount++;
                if (kitCount >= 400) {
                    await kitBatch.commit();
                    kitBatch = writeBatch(db);
                    kitCount = 0;
                }
            }
            if (kitCount > 0) await kitBatch.commit();

            return { success: true, message: "All Master Cost Prices reset to 0 successfully." };
        } catch (error) {
            console.error("Cost Price Reset Failed:", error);
            return { success: false, message: error.message };
        }
    };

    const recalculateAllCommissions = async () => {
        try {
            console.log("🚀 Starting Full Commission Recalculation...");

            // 1. Initialize local state for rebuilding
            const userDataMap = {};
            users.forEach(u => {
                userDataMap[u.id] = {
                    walletGross: 0,
                    totalEarned: 0,
                    firestoreId: u.firestoreId || u.id
                };
            });

            const levels = Array.isArray(settings?.commissionLevels) ? settings.commissionLevels : [5, 4, 3, 2, 1, 1, 0.5];
            const selfBonusPercent = Number(settings?.selfPurchaseBonus ?? 10);
            const newLogs = [];

            // 2. Process all successful orders to calculate Gross Earnings
            const successfulOrders = orders.filter(o => ['Delivered', 'Completed'].includes(o.status));
            console.log(`📦 Processing ${successfulOrders.length} successful orders for recalculation...`);

            successfulOrders.forEach(order => {
                const amountNum = Number(order.totalAmount);
                if (isNaN(amountNum) || amountNum <= 0) return;

                const buyer = findUserById(order.userId);
                if (!buyer) return;

                // Self Bonus calculation
                if (selfBonusPercent > 0) {
                    const bonus = Number(((amountNum * selfBonusPercent) / 100).toFixed(2));
                    if (userDataMap[buyer.id]) {
                        userDataMap[buyer.id].walletGross += bonus;
                        userDataMap[buyer.id].totalEarned += bonus;
                        newLogs.push({
                            level: 0, sponsorId: buyer.id, amount: bonus, percentage: selfBonusPercent,
                            note: 'Self Bonus (Recalculated)', sourceMemberId: order.userId,
                            date: order.date || new Date().toISOString()
                        });
                    }
                }

                // Upline Commission calculation
                let currentRecipientId = buyer.sponsorId;
                const maxLevels = levels.length;
                for (let level = 0; level < maxLevels; level++) {
                    if (!currentRecipientId) break;
                    const recipient = findUserById(currentRecipientId);
                    if (!recipient) break;

                    const percentage = levels[level] || 0;
                    const commission = Number(((amountNum * percentage) / 100).toFixed(2));

                    if (commission > 0 && userDataMap[recipient.id]) {
                        userDataMap[recipient.id].walletGross += commission;
                        userDataMap[recipient.id].totalEarned += commission;
                        newLogs.push({
                            level: level + 1, sponsorId: recipient.id, amount: commission, percentage,
                            note: `Level ${level + 1} (Recalculated)`, sourceMemberId: order.userId,
                            date: order.date || new Date().toISOString()
                        });
                    }
                    currentRecipientId = recipient.sponsorId;
                }
            });

            // 3. Process All Completed/Paid Payouts to subtract from Wallet
            const completedPayouts = payouts.filter(p => ['Completed', 'Paid'].includes(p.status));
            console.log(`💸 Processing ${completedPayouts.length} completed/paid payouts...`);
            completedPayouts.forEach(p => {
                const recipient = findUserById(p.userId);
                if (recipient && userDataMap[recipient.id]) {
                    userDataMap[recipient.id].walletGross -= Number(p.amount);
                }
            });

            // 4. Cloud Persistence
            // Step A: Clear existing commission logs
            console.log("🧹 Clearing old commission logs...");
            const logsSnap = await getDocs(collection(db, "commissionLogs"));
            let logDeleteBatch = writeBatch(db);
            let logDelCount = 0;
            for (const docSnap of logsSnap.docs) {
                logDeleteBatch.delete(docSnap.ref);
                logDelCount++;
                if (logDelCount >= 400) {
                    await logDeleteBatch.commit();
                    logDeleteBatch = writeBatch(db);
                    logDelCount = 0;
                }
            }
            if (logDelCount > 0) await logDeleteBatch.commit();

            // Step B: Update user balances in database
            console.log("💾 Updating user balances in database...");
            let userUpdateBatch = writeBatch(db);
            let userCount = 0;
            for (const uid of Object.keys(userDataMap)) {
                const data = userDataMap[uid];

                // Safety Guard: Ensure numbers are valid and finite
                const finalWallet = Number(data.walletGross.toFixed(2));
                const finalEarned = Number(data.totalEarned.toFixed(2));

                userUpdateBatch.update(doc(db, "users", data.firestoreId), {
                    wallet: isFinite(finalWallet) ? finalWallet : 0,
                    totalEarned: isFinite(finalEarned) ? finalEarned : 0
                });
                userCount++;
                if (userCount >= 400) {
                    await userUpdateBatch.commit();
                    userUpdateBatch = writeBatch(db);
                    userCount = 0;
                }
            }
            if (userCount > 0) await userUpdateBatch.commit();

            // Step C: Bulk write new logs in chunks
            console.log(`📝 Writing ${newLogs.length} new commission logs...`);
            const logChunks = [];
            for (let i = 0; i < newLogs.length; i += 400) {
                logChunks.push(newLogs.slice(i, i + 400));
            }
            for (const chunk of logChunks) {
                const batch = writeBatch(db);
                chunk.forEach(logData => {
                    const logRef = doc(collection(db, "commissionLogs"));
                    batch.set(logRef, {
                        ...logData,
                        id: Date.now() + Math.random()
                    });
                });
                await batch.commit();
            }

            console.log("✅ Recalculation Successful!");
            return { success: true, message: `Recalculation Successful! Processed ${successfulOrders.length} orders and ${completedPayouts.length} payouts.` };
        } catch (error) {
            console.error("Recalculation Failed:", error);
            return { success: false, message: "Recalculation Error: " + error.message };
        }
    };

    // --- Missing Functions Implementation ---
    const addKit = async (kitData) => {
        const id = Date.now();
        const initialStock = Number(kitData.stock || 0);

        // VALIDATION: If creating with stock, check ingredients
        if (initialStock > 0 && kitData.contents?.length > 0) {
            for (const item of kitData.contents) {
                const prod = products.find(p => String(p.id) === String(item.productId));
                if (!prod) continue;
                const required = item.qty * initialStock;
                if ((prod.stock || 0) < required) {
                    return {
                        success: false,
                        message: `Insufficient stock for ${prod.name} to create initial kit stock. Required: ${required}, Available: ${prod.stock}`
                    };
                }
            }

            // DEDUCT INGREDIENTS
            for (const item of kitData.contents) {
                const prod = products.find(p => String(p.id) === String(item.productId));
                if (prod?.firestoreId) {
                    const newProdStock = Number(prod.stock || 0) - (item.qty * initialStock);
                    await updateDoc(doc(db, "products", prod.firestoreId), { stock: newProdStock });

                    const actorName = adminUser?.name || memberUser?.name || 'Admin';
                    await logStockMovement('product', prod.id, prod.name, -(item.qty * initialStock), `Used in New Kit Creation (${kitData.name})`, actorName);
                }
            }
        }

        await addDoc(collection(db, "kits"), { ...kitData, price: Number(kitData.price), stock: initialStock, id });

        if (initialStock > 0) {
            const actorName = adminUser?.name || memberUser?.name || 'Admin';
            await logStockMovement('kit', id, kitData.name, initialStock, 'Initial Stock', actorName);
        }

        return { success: true };
    };

    const assembleKit = async (kitId, quantity) => {
        try {
            const kit = kits.find(k => String(k.id) === String(kitId));
            if (!kit) return { success: false, message: `Kit not found (ID: ${kitId})` };

            const qtyToAssemble = Number(quantity);
            if (qtyToAssemble <= 0) return { success: false, message: "Invalid quantity" };

            // 1. Check Ingredient Stock
            const missingIngredients = [];
            for (const item of (kit.contents || [])) {
                const prod = products.find(p => String(p.id) === String(item.productId));
                if (!prod) {
                    missingIngredients.push(`Unknown Product (ID: ${item.productId})`);
                    continue;
                }
                const needed = item.qty * qtyToAssemble;
                if ((prod.stock || 0) < needed) {
                    missingIngredients.push(`${prod.name} (Need ${needed}, Have ${prod.stock || 0})`);
                }
            }

            if (missingIngredients.length > 0) {
                return {
                    success: false,
                    message: `Insufficient Stock: ${missingIngredients.join(', ')}`
                };
            }

            // 2. Deduct Ingredients & Add Kit Stock
            const batchPromises = [];
            const logItems = [];

            // A. Deduct Ingredients
            for (const item of (kit.contents || [])) {
                // Determine product by type-agnostic ID match
                const prod = products.find(p => String(p.id) === String(item.productId));

                // Safety check: If product exists but has no Firestore ID, it cannot be updated.
                if (!prod || !prod.firestoreId) {
                    return { success: false, message: `Critical Error: Product "${prod?.name || item.productId}" has no database reference (firestoreId).` };
                }

                if (prod && prod.firestoreId) {
                    const deductQty = Number(item.qty) * qtyToAssemble;
                    const newStock = Math.max(0, (Number(prod.stock) || 0) - deductQty);

                    // Add update to batch promises
                    batchPromises.push(updateDoc(doc(db, "products", prod.firestoreId), { stock: newStock }));

                    // Log Movement (Fire and forget, but important)
                    const actorName = adminUser?.name || memberUser?.name || 'Admin';
                    logStockMovement('product', prod.id, prod.name, -deductQty, `Used in Kit (${kit.name})`, actorName);

                    logItems.push({ name: prod.name, qty: deductQty });
                }
            }

            // B. Add Kit Stock
            if (kit.firestoreId) {
                const newKitStock = (kit.stock || 0) + qtyToAssemble;
                batchPromises.push(updateDoc(doc(db, "kits", kit.firestoreId), { stock: newKitStock }));
                const actorName = adminUser?.name || memberUser?.name || 'Admin';
                logStockMovement('kit', kit.id, kit.name, qtyToAssemble, 'Assembled from Stock', actorName);
            }

            // C. Log Assembly
            const assemblyLog = {
                id: Date.now(),
                date: new Date().toISOString(),
                kitId: kit.id,
                kitName: kit.name,
                quantity: qtyToAssemble,
                itemsConsumed: logItems,
                assembledBy: adminUser?.name || memberUser?.name || 'Admin'
            };
            batchPromises.push(addDoc(collection(db, "assemblyLogs"), assemblyLog));

            await Promise.all(batchPromises);
            return { success: true, message: `Successfully assembled ${qtyToAssemble} ${kit.name}(s).` };

        } catch (error) {
            console.error("Assembly Failed:", error);
            return { success: false, message: error.message };
        }
    };

    const syncStockFromKits = async (dryRun = false) => {
        try {
            console.log("Starting Retroactive Stock Sync...", dryRun ? "(Dry Run)" : "");

            if (!kits || !Array.isArray(kits)) {
                return { success: false, message: "Kits data not loaded or invalid." };
            }

            const stockUpdates = {};
            const debugLogs = [];
            let totalKitsProcessed = 0;

            debugLogs.push(`Total Kits Found: ${kits.length}`);
            const alreadySyncedKits = [];

            for (const k of kits) {
                const currentStock = Number(k.stock || 0);

                // IDEMPOTENCY CHECK: Skip if already synced
                if (k.stockSynced) {
                    debugLogs.push(`Skipping Kit: ${k.name} (Already Synced)`);
                    alreadySyncedKits.push(k.name);
                    continue;
                }

                debugLogs.push(`Checking Kit: ${k.name} (ID: ${k.id}), Stock: ${currentStock}`);

                if (currentStock > 0 && k.contents?.length > 0) {
                    totalKitsProcessed++;

                    for (const item of k.contents) {
                        const prod = products.find(p => String(p.id) === String(item.productId));

                        if (!prod) {
                            debugLogs.push(`  - Skipped Item: Product ID ${item.productId} not found.`);
                            continue;
                        }
                        if (!prod.firestoreId) {
                            debugLogs.push(`  - Skipped Item: Product ${prod.name} missing Firestore ID.`);
                            continue;
                        }

                        const needed = Number(item.qty) * currentStock;
                        debugLogs.push(`  - Deducting: ${prod.name} | Qty: ${item.qty} * ${currentStock} = ${needed}`);

                        if (!stockUpdates[prod.id]) {
                            stockUpdates[prod.id] = {
                                prod: prod,
                                deductQty: 0,
                                reasons: []
                            };
                        }
                        stockUpdates[prod.id].deductQty += needed;
                        stockUpdates[prod.id].reasons.push(`${currentStock} ${k.name}`);
                    }
                } else {
                    debugLogs.push(`  - Skipped: Stock is 0 or no contents.`);
                }
            }

            if (dryRun) {
                return {
                    success: true,
                    dryRun: true,
                    totalKits: totalKitsProcessed,
                    debugLogs: debugLogs,
                    updates: Object.values(stockUpdates).map(u => ({
                        name: u.prod.name,
                        deduct: u.deductQty,
                        reasons: u.reasons
                    })),
                    skipped: alreadySyncedKits // Return skipped list
                };
            }

            // Apply Deductions using WriteBatch (Atomic)
            const batch = writeBatch(db);
            let opCount = 0;
            const updatesCount = Object.keys(stockUpdates).length;

            for (const prodId in stockUpdates) {
                const { prod, deductQty, reasons } = stockUpdates[prodId];

                const currentProdStock = Number(prod.stock || 0);
                const newStock = currentProdStock - deductQty;

                // 1. Update Product Stock
                const prodRef = doc(db, "products", prod.firestoreId);
                batch.update(prodRef, { stock: newStock });
                opCount++;

                // 2. Add Log Entry
                const logRef = doc(collection(db, "stockLogs"));
                batch.set(logRef, {
                    id: Date.now() + Math.random(),
                    date: new Date().toISOString(),
                    itemType: 'product',
                    itemId: String(prod.id),
                    itemName: prod.name,
                    quantity: -deductQty,
                    action: 'Deducted',
                    reason: `Sync (Used in Kit): ${reasons.join(', ')}`,
                    by: 'Admin (System Sync)' // Safe hardcoded string
                });
                opCount++;
            }

            // 3. Mark Kits as SYNCED (Prevent double deduction)
            for (const k of kits) {
                if (k.stockSynced) continue; // Skip already synced
                if (k.stock > 0 && k.firestoreId) {
                    batch.update(doc(db, "kits", k.firestoreId), { stockSynced: true });
                    opCount++;
                }
            }

            if (opCount > 0) {
                await batch.commit();
                return { success: true, message: `Synced stock for ${totalKitsProcessed} kits. Updated ${updatesCount} products.` };
            } else {
                return { success: true, message: "No active kits or ingredients found to sync." };
            }
        } catch (e) {
            console.error("Sync Failed:", e);
            return { success: false, message: e.message };
        }
    };

    const updateKit = async (id, updates) => {
        const kit = kits.find(k => String(k.id) === String(id));
        if (!kit?.firestoreId) return { success: false, message: "Kit not found" };

        const oldStock = Number(kit.stock || 0);
        let newStock;

        if (updates.stockToAdd !== undefined) {
            newStock = oldStock + Number(updates.stockToAdd);
        } else {
            newStock = updates.stock !== undefined ? Number(updates.stock) : oldStock;
        }

        const oldContents = kit.contents || [];
        const newContents = updates.contents || oldContents;

        // 1. Calculate Total Ingredient Requirements (Old vs New)
        const oldReqs = {};
        for (const item of oldContents) {
            const pid = String(item.productId);
            oldReqs[pid] = (oldReqs[pid] || 0) + (Number(item.qty) * oldStock);
        }

        const newReqs = {};
        for (const item of newContents) {
            const pid = String(item.productId);
            newReqs[pid] = (newReqs[pid] || 0) + (Number(item.qty) * newStock);
        }

        // 2. Identify Diffs (Products needing deduction or return)
        const productDiffs = {};
        const allProdIds = new Set([...Object.keys(oldReqs), ...Object.keys(newReqs)]);
        const missingProducts = [];

        for (const pid of allProdIds) {
            const requiredOld = oldReqs[pid] || 0;
            const requiredNew = newReqs[pid] || 0;
            const diff = requiredNew - requiredOld; // +ve = Need more (Deduct), -ve = Return

            if (diff !== 0) {
                const prod = products.find(p => String(p.id) === String(pid));
                if (!prod) {
                    // Logic gap: If product deleted but used in old kit, we can't return stock easily. 
                    // But if it's new requirement, we block.
                    if (diff > 0) {
                        return {
                            success: false,
                            message: `Measurement Error: Product (ID: ${pid}) used in this kit was not found in inventory. It may have been deleted.`
                        };
                    }
                    continue;
                }

                // VALIDATION for Deductions
                if (diff > 0) {
                    if ((prod.stock || 0) < diff) {
                        return {
                            success: false,
                            message: `Insufficient stock for ${prod.name}. Need ${diff}, Have ${prod.stock}`
                        };
                    }
                }

                productDiffs[pid] = { prod, diff };
            }
        }

        if (missingProducts.length > 0) {
            return { success: false, message: `Cannot update: ${missingProducts.join(', ')}` };
        }

        // 3. APPLY UPDATES
        const actorName = adminUser?.name || memberUser?.name || 'Admin';
        const batchPromises = [];

        for (const pid in productDiffs) {
            const { prod, diff } = productDiffs[pid];
            if (prod.firestoreId) {
                const newProdStock = Number(prod.stock || 0) - diff;
                batchPromises.push(updateDoc(doc(db, "products", prod.firestoreId), { stock: newProdStock }));

                // Log
                const action = diff > 0 ? 'Deducted' : 'Returned';
                const reason = diff > 0
                    ? `Kit Update/Assembly (${kit.name})`
                    : `Kit Update/Disassembly (${kit.name})`;

                // Note: logStockMovement is async fire-and-forget usually, but here we await to ensure order? 
                // Context functions are async but logStockMovement is helper. 
                // We'll call it parallel.
                logStockMovement('product', prod.id, prod.name, -diff, reason, actorName);
            }
        }

        // 4. Update Kit
        await Promise.all(batchPromises); // Wait for product updates

        // Clean up updates object before saving to Firestore
        const finalUpdates = { ...updates, stock: newStock };
        delete finalUpdates.stockToAdd;

        await updateDoc(doc(db, "kits", kit.firestoreId), finalUpdates);

        // Log Kit Stock Change specifically if stock changed
        if (oldStock !== newStock) {
            const stockDiff = newStock - oldStock;
            await logStockMovement('kit', kit.id, kit.name, stockDiff, 'Manual Update / Assembly', actorName);
        }

        return { success: true };
    };



    const purgeAndResetInventory = async () => {
        try {
            const collections = ["products", "kits"];
            for (const colName of collections) {
                const q = query(collection(db, colName));
                const snapshot = await getDocs(q);
                const batch = writeBatch(db);
                let counter = 0;

                for (const docSnap of snapshot.docs) {
                    batch.delete(docSnap.ref);
                    counter++;
                    if (counter >= 400) {
                        await batch.commit();
                        counter = 0;
                    }
                }
                if (counter > 0) await batch.commit();
            }

            // Reset local state to empty (optional, real-time listener should catch it)
            setProducts([]);
            setKits([]);

            return { success: true, message: "Inventory Purged." };
        } catch (e) {
            return { success: false, message: e.message };
        }
    };


    const unpackAllKits = async () => {
        try {
            console.log("Starting Unpack All Kits (Migration to Virtual)...");
            const batch = writeBatch(db);
            let opCount = 0;
            let kitsProcessed = 0;

            for (const kit of kits) {
                const stock = Number(kit.stock || 0);
                if (stock > 0 && kit.firestoreId) {
                    kitsProcessed++;

                    // 1. Return Ingredients
                    if (kit.contents && kit.contents.length > 0) {
                        for (const item of kit.contents) {
                            const prod = products.find(p => String(p.id) === String(item.productId));
                            if (prod && prod.firestoreId) {
                                const qtyToReturn = Number(item.qty) * stock;
                                const currentProdStock = Number(prod.stock || 0);
                                const newProdStock = currentProdStock + qtyToReturn;

                                batch.update(doc(db, "products", prod.firestoreId), { stock: newProdStock });
                                opCount++;

                                // Log Return
                                const logRef = doc(collection(db, "stockLogs"));
                                batch.set(logRef, {
                                    id: Date.now() + Math.random(),
                                    date: new Date().toISOString(),
                                    itemType: 'product',
                                    itemId: String(prod.id),
                                    itemName: prod.name,
                                    quantity: qtyToReturn,
                                    action: 'Returned',
                                    reason: `Migration: Unpacked ${stock} ${kit.name}`,
                                    by: adminUser?.name || 'Admin',
                                    performedBy: adminUser?.name || 'Admin'
                                });
                                opCount++;
                            }
                        }
                    }

                    // 2. Zero out Kit Stock
                    batch.update(doc(db, "kits", kit.firestoreId), { stock: 0 });
                    opCount++;

                    // Log Kit Zeroing
                    const kLogRef = doc(collection(db, "stockLogs"));
                    batch.set(kLogRef, {
                        id: Date.now() + Math.random(),
                        date: new Date().toISOString(),
                        itemType: 'kit',
                        itemId: String(kit.id),
                        itemName: kit.name,
                        quantity: -stock,
                        action: 'Deducted',
                        reason: 'Migration: Converted to Virtual Stock',
                        by: adminUser?.name || 'Admin',
                        performedBy: adminUser?.name || 'Admin'
                    });
                    opCount++;
                }
            }

            if (opCount > 0) {
                await batch.commit();
                return { success: true, message: `Successfully unpacked ${kitsProcessed} kits types into product inventory.` };
            } else {
                return { success: true, message: "No physical kits found to unpack." };
            }

        } catch (e) {
            console.error("Unpack All Failed:", e);
            return { success: false, message: e.message };
        }
    };

    const revertAllKits = async () => {
        try {
            // 0. Check for recent Reverts (Safety)
            const fiveMinsAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
            const qRevert = query(
                collection(db, "stockLogs"),
                where("date", ">=", fiveMinsAgo),
                // where("action", "==", "Revert")
            );
            const snapRevert = await getDocs(qRevert);
            const recentReverts = snapRevert.docs.some(d => d.data().action === 'Revert');

            if (recentReverts) {
                // Return success but warning, so UI doesn't retry loop
                return { success: false, message: "A Revert action was already performed in the last 5 minutes. Please wait before trying again to avoid double-counting." };
            }

            // 1. Get logs from last 30 DAYS (Broadened window)
            const searchWindow = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
            const q = query(
                collection(db, "stockLogs"),
                where("date", ">=", searchWindow)
            );

            const snap = await getDocs(q);

            // Allow generic "Migration" check - Case Insensitive
            const unpackLogs = snap.docs
                .map(d => d.data())
                .filter(d =>
                    (d.reason && String(d.reason).toLowerCase().includes("migration")) &&
                    (d.action === "Returned" || d.quantity > 0) // Broaden to positive qty if action name differs
                );

            const kitZeroLogs = snap.docs
                .map(d => d.data())
                .filter(d =>
                    d.itemType === 'kit' &&
                    (d.reason && String(d.reason).toLowerCase().includes("migration"))
                );

            if (unpackLogs.length === 0 && kitZeroLogs.length === 0) {
                return { success: false, message: `No migration logs found in last 30 days. Checked ${snap.size} logs.` };
            }

            // 2. Aggregate Product Deductions (Undo the Return)
            const productDeductions = {};
            unpackLogs.forEach(log => {
                const pid = log.itemId;
                if (!productDeductions[pid]) productDeductions[pid] = 0;
                productDeductions[pid] += Number(log.quantity);
            });

            // 3. Aggregate Kit Restoration (Undo the Zeroing)
            const kitRestorations = {};
            kitZeroLogs.forEach(log => {
                const kid = log.itemId;
                if (!kitRestorations[kid]) kitRestorations[kid] = 0;
                // Log quantity was negative (deducted), so use abs
                kitRestorations[kid] += Math.abs(Number(log.quantity));
            });

            // 4. Apply via Transaction (Fix: Separate Reads and Writes)
            let prodCount = 0;
            let kitCount = 0;

            await runTransaction(db, async (transaction) => {
                const productUpdates = [];
                const kitUpdates = [];

                // --- READ PHASE ---

                // 1. Prepare Product Reads
                for (const pid of Object.keys(productDeductions)) {
                    const prod = products.find(p => String(p.id) === String(pid));
                    if (!prod || !prod.firestoreId) continue;
                    const ref = doc(db, "products", prod.firestoreId);
                    const docSnap = await transaction.get(ref);
                    if (docSnap.exists()) {
                        productUpdates.push({
                            ref: ref,
                            current: Number(docSnap.data().stock || 0),
                            deduct: productDeductions[pid]
                        });
                    }
                }

                // 2. Prepare Kit Reads
                for (const kid of Object.keys(kitRestorations)) {
                    const kit = kits.find(k => String(k.id) === String(kid));
                    if (!kit || !kit.firestoreId) continue;
                    const ref = doc(db, "kits", kit.firestoreId);
                    const docSnap = await transaction.get(ref);
                    if (docSnap.exists()) {
                        kitUpdates.push({
                            ref: ref,
                            current: Number(docSnap.data().stock || 0),
                            add: kitRestorations[kid]
                        });
                    }
                }

                // --- WRITE PHASE ---

                // 3. Execute Product Updates
                for (const update of productUpdates) {
                    const newStock = Math.max(0, update.current - update.deduct);
                    transaction.update(update.ref, { stock: newStock });
                    prodCount++;
                }

                // 4. Execute Kit Updates
                for (const update of kitUpdates) {
                    const newStock = update.current + update.add;
                    transaction.update(update.ref, { stock: newStock });
                    kitCount++;
                }
            });

            // 5. Log the Reversion
            const batch = writeBatch(db);

            if (prodCount > 0 || kitCount > 0) {
                const logRef = doc(collection(db, "stockLogs"));
                batch.set(logRef, {
                    id: Date.now(),
                    date: new Date().toISOString(),
                    itemType: 'system',
                    itemId: 'revert_migration',
                    itemName: 'System',
                    quantity: 0,
                    action: 'Revert',
                    reason: `Reverted Migration: Restored ${kitCount} kits, Deducted ${prodCount} products.`,
                    performedBy: adminUser?.name || 'Admin',
                    by: adminUser?.name || 'Admin'
                });
                await batch.commit();
            }

            return { success: true, message: `Reversion Complete. Restored ${kitCount} kits, deducted ${prodCount} products.` };

        } catch (e) {
            console.error("Reversion Failed:", e);
            return { success: false, message: e.message };
        }
    };

    const undoExcessRepairs = async () => {
        try {
            console.log("Starting Undo Excess Repairs...");
            // Look back 30 days
            const searchWindow = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

            // 1. Find Repair Logs 
            const q = query(
                collection(db, "stockLogs"),
                where("date", ">=", searchWindow)
            );
            const snap = await getDocs(q);

            // Filter strictly for Repair actions
            const repairLogs = snap.docs
                .map(d => d.data())
                .filter(d => d.action === 'Repair' || (d.reason && String(d.reason).includes('Repaired Stock')));

            const repairCount = repairLogs.length;

            if (repairCount === 0) {
                return { success: false, message: `No 'Repair' actions found in last 30 days. (Scanned ${snap.size} logs)` };
            }

            // 2. Find Migration Logs (To calculate amounts per repair)
            const migrationLogs = snap.docs
                .map(d => d.data())
                .filter(d =>
                    d.reason && String(d.reason).toLowerCase().includes("migration") &&
                    (d.action === "Returned" || d.quantity > 0)
                );

            if (migrationLogs.length === 0) {
                return { success: false, message: `Found ${repairCount} Repairs, but NO Migration logs (Unpack logs) to calculate values.` };
            }

            // 3. Calculate Base Amount (What 1 Repair did)
            const baseReversalAmounts = {};
            migrationLogs.forEach(log => {
                const pid = log.itemId;
                if (!baseReversalAmounts[pid]) baseReversalAmounts[pid] = 0;
                baseReversalAmounts[pid] += Number(log.quantity);
            });

            // 4. Total Deductions = Base * RepairCount
            const totalDeductions = {};
            Object.keys(baseReversalAmounts).forEach(pid => {
                totalDeductions[pid] = baseReversalAmounts[pid] * repairCount;
            });

            // 5. Execute Deduction
            let updatedCount = 0;
            await runTransaction(db, async (transaction) => {
                const productUpdates = [];
                for (const pid of Object.keys(totalDeductions)) {
                    const prod = products.find(p => String(p.id) === String(pid));
                    if (!prod || !prod.firestoreId) continue;

                    const ref = doc(db, "products", prod.firestoreId);
                    const docSnap = await transaction.get(ref);
                    if (docSnap.exists()) {
                        productUpdates.push({
                            ref: ref,
                            current: Number(docSnap.data().stock || 0),
                            deduct: totalDeductions[pid]
                        });
                    }
                }

                for (const update of productUpdates) {
                    const newStock = Math.max(0, update.current - update.deduct);
                    transaction.update(update.ref, { stock: newStock });
                    updatedCount++;
                }
            });

            // 6. Log it
            if (updatedCount > 0) {
                const batch = writeBatch(db);
                const logRef = doc(collection(db, "stockLogs"));
                batch.set(logRef, {
                    id: Date.now(),
                    date: new Date().toISOString(),
                    itemType: 'system',
                    itemId: 'undo_excess_repair',
                    itemName: 'System',
                    quantity: 0,
                    action: 'Undo Repair',
                    reason: `Emergency Undo: Reversed ${repairCount} Repair actions.`,
                    performedBy: adminUser?.name || 'Admin',
                    by: adminUser?.name || 'Admin'
                });
                await batch.commit();
            }

            return { success: true, message: `Fixed! Undid ${repairCount} accidental repairs.` };

        } catch (e) {
            console.error("Undo Excess Failed:", e);
            return { success: false, message: e.message };
        }
    };

    const deleteKit = async (id, returnStock = false) => {
        try {
            const kit = kits.find(k => k.id === id);
            if (!kit) return { success: false, message: "Kit not found" };

            const batch = writeBatch(db);

            // 1. Return Stock Logic
            if (returnStock && kit.stock > 0 && kit.contents?.length > 0) {
                for (const item of kit.contents) {
                    const prod = products.find(p => String(p.id) === String(item.productId));
                    if (prod && prod.firestoreId) {
                        const qtyToReturn = Number(item.qty) * Number(kit.stock);
                        const newStock = Number(prod.stock || 0) + qtyToReturn;

                        batch.update(doc(db, "products", prod.firestoreId), { stock: newStock });

                        // Log
                        const logRef = doc(collection(db, "stockLogs"));
                        batch.set(logRef, {
                            id: Date.now() + Math.random(),
                            date: new Date().toISOString(),
                            itemType: 'product',
                            itemId: String(prod.id),
                            itemName: prod.name,
                            quantity: qtyToReturn,
                            action: 'Returned',
                            reason: `Kit Deleted: ${kit.name} (${kit.stock} units)`,
                            by: adminUser?.name || 'Admin'
                        });
                    }
                }
            }

            // 2. Delete Kit
            if (kit.firestoreId) {
                batch.delete(doc(db, "kits", kit.firestoreId));
            }

            await batch.commit();
            return { success: true, message: returnStock ? "Kit deleted and stock returned." : "Kit deleted." };

        } catch (error) {
            console.error("Error deleting kit:", error);
            return { success: false, message: error.message };
        }
    };

    const repairStockForNewPolicy = async () => {
        try {
            console.log("🛠️ Starting Stock Repair for New Policy...");
            const ordersSnap = await getDocs(collection(db, "orders"));
            const batch = writeBatch(db);
            let restoredCount = 0;
            let updatedOrders = 0;

            for (const docSnap of ordersSnap.docs) {
                const order = { ...docSnap.data(), firestoreId: docSnap.id };

                // 1. Handle Orders that WERE deducted but are NOT delivered (Restore them)
                if (order.isStockDeducted === undefined && !['Delivered', 'Completed', 'Cancelled', 'Returned'].includes(order.status)) {
                    console.log(`Restoring stock for Pending Order ${order.id}...`);
                    if (order.items && order.items.length > 0) {
                        for (const item of order.items) {
                            const qty = Number(item.quantity) || 0;
                            if (item.type === 'kit' || String(item.id).startsWith('kit-')) {
                                const kitId = String(item.id).replace('kit-', '');
                                const kit = kits.find(k => String(k.id) === kitId);
                                if (kit && kit.firestoreId) {
                                    const newStock = (Number(kit.stock) || 0) + qty;
                                    batch.update(doc(db, "kits", kit.firestoreId), { stock: newStock });
                                    logStockMovement('kit', kit.id, kit.name, qty, `Repair: Restore pending stock (Order ${order.id})`, 'System Repair');
                                }
                            } else {
                                const prod = products.find(p => String(p.id) === String(item.id));
                                if (prod && prod.firestoreId) {
                                    const newStock = (Number(prod.stock) || 0) + qty;
                                    batch.update(doc(db, "products", prod.firestoreId), { stock: newStock });
                                    logStockMovement('product', prod.id, prod.name, qty, `Repair: Restore pending stock (Order ${order.id})`, 'System Repair');
                                }
                            }
                        }
                    }
                    batch.update(docSnap.ref, { isStockDeducted: false }); // Mark explicitly for new system
                    restoredCount++;
                    updatedOrders++;
                }
                // 2. Handle Orders that WERE deducted and ARE delivered/cancelled (Mark as deducted so we don't touch them)
                else if (order.isStockDeducted === undefined) {
                    const isAlreadyHandled = ['Delivered', 'Completed', 'Cancelled', 'Returned'].includes(order.status);
                    if (isAlreadyHandled) {
                        // Historically deducted, or cancelled (which restored stock in old system)
                        // In both cases, the "deduction lifecycle" is over.
                        batch.update(docSnap.ref, { isStockDeducted: order.status === 'Cancelled' ? false : true });
                        updatedOrders++;
                    }
                }
            }

            if (updatedOrders > 0) {
                await batch.commit();
                console.log(`✅ Repair Finished! Restored stock for ${restoredCount} orders. Updated ${updatedOrders} total order flags.`);
                return { success: true, message: `Repair Finished! Restored stock for ${restoredCount} pending orders.` };
            } else {
                return { success: true, message: "No inconsistent orders found. Everything looks correct!" };
            }
        } catch (error) {
            console.error("Stock Repair Failed:", error);
            return { success: false, message: "Repair Error: " + error.message };
        }
    };

    return (
        <AppContext.Provider value={{
            isAppReady,
            settings, users, adminUser, memberUser, currentUser: adminUser || memberUser,
            login, logout, registerUser, updateSettings, processPayout, distributeCommissions,
            updateUserStatus, updateUser, deleteUser, payouts, updatePayout, refreshSystemData,
            resetFinancialData, resetCommissionLogic, recalculateAllCommissions, commissionLogs, products, addProduct, updateProduct, deleteProduct,
            orders, placeOrder, updateOrder, deleteOrder,
            getTeamStats, getDetailedBusinessStats,
            kits, updateKit, addKit, deleteKit, syncAllKitPrices, assembleKit, assemblyLogs, siteContent, updateSiteContent, syncStockFromKits, unpackAllKits, revertAllKits,
            stockLogs, logStockMovement,
            expenses, addExpense, deleteExpense, editExpense, // Exporting Expenses Features
            findUserById,

            legalDocs, addLegalDoc, deleteLegalDoc, fixTreeHierarchy,
            contactMessages, addContactMessage, deleteContactMessage,
            purgeAndResetInventory, resetStockOnly, resetAllCostPrices
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => useContext(AppContext);
